import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StockChart } from './components/StockChart';
import { ControlPanel } from './components/ControlPanel';
import { DateRangeSelector } from './components/DateRangeSelector';
import { DateRangeNavigator } from './components/DateRangeNavigator';
import Header from './components/Header';
import Snackbar, { SnackbarVariant } from './components/Snackbar';
import WatchlistAddEditModal from './components/WatchlistAddEditModal';
import QuickAddPortfolioHoldingModal from './components/QuickAddPortfolioHoldingModal';
import AdvancedChartScreen from './screens/AdvancedChartScreen';
import PortfoliosScreen from './screens/PortfoliosScreen';
import WatchlistScreen from './screens/WatchlistScreen';
import AlertsScreen from './screens/AlertsScreen';
import RecordingsScreen from './screens/RecordingsScreen';
import ResearchSummaryScreen from './screens/research/ResearchSummaryScreen';
import ResearchInsightsScreen from './screens/research/ResearchInsightsScreen';
import ResearchKeyMetricsScreen from './screens/research/ResearchKeyMetricsScreen';
import ResearchBalanceSheetScreen from './screens/research/ResearchBalanceSheetScreen';
import ResearchIncomeStatementScreen from './screens/research/ResearchIncomeStatementScreen';
import ResearchIncomeStatementFiscalScreen from './screens/research/ResearchIncomeStatementFiscalScreen';
import ResearchCashFlowScreen from './screens/research/ResearchCashFlowScreen';
import ResearchGrowthFiscalScreen from './screens/research/ResearchGrowthFiscalScreen';
import ResearchBackTestingScreen from './screens/research/ResearchBackTestingScreen';
import ResearchValuationScreen from './screens/research/ResearchValuationScreen';
import ResearchDeskScreen from './screens/research/ResearchDeskScreen';
import VInvestAIScreen from './screens/research/VInvestAIScreen';
import AdminShell from './screens/admin/AdminShell';
import { StockDataPoint } from './data/mockStockData';
import { config } from './config/config';
import stocksList from './data/stocksList.json';
import { fetchHistoricalPrices } from './services/stockApi';
import { adminApi, isUnauthorized } from './services/adminApi';
import { 
  calculateTechnicalIndicators, 
  filterDataByDateRange
} from './utils/technicalIndicators';

interface AppState {
  selectedStock: string;
  selectedRange: string;
  chartType: 'line' | 'candlestick';
  theme: 'light' | 'dark';
  currentScreen: 'main' | 'advanced';
  indicators: {
    showEMA12: boolean;
    showEMA26: boolean;
    showEMA50: boolean;
    showEMA200: boolean;
    showVolume: boolean;
  };
  customStartDate?: Date;
  customEndDate?: Date;
  navigatorRange: [Date, Date];
}

const STARTUP_EULA_HEBREW_TEXT = `הסכם מנוי, תנאי שימוש והסתייגות מאחריות

הסכם זה נערך בין:

המפעיל, אלון הזה, ע.מ 027913326, מרחוב משה סנה 12, פתח תקווה

לבין

המנוי, שפרטיו וחתימתו מופיעים בסוף מסמך זה או ברישום האלקטרוני המשויך אליו.

הקדמה והסכמה

בחתימה חד-פעמית על הסכם זה, ולחלופין באישור אלקטרוני מפורש בעת ההצטרפות לשירות, המנוי מאשר כי קרא את ההסכם, הבין אותו, והסכים להיות מחויב לכל הוראותיו. אישור אלקטרוני כאמור, בצירוף תיעוד טכני של מועד האישור, ייחשב להסכמה מחייבת לכל דבר ועניין.

הגדרות השירות

"השירות" כולל, בין היתר, גישה לאתר, לאזור האישי, לנתונים פיננסיים, גרפים, מסכים, יחסים פיננסיים, הערכות שווי, מודלי חישוב, רשימות מעקב, התראות קניה ומכירה, תכנים אנליטיים, תוכן לימודי, כלי השוואה, ממשקי משתמש, קבצים, תיעוד, וכל שירות נלווה אחר שמספק המפעיל מעת לעת.

אופי השירות והיעדר ייעוץ השקעות

השירות נועד למחקר, לימוד, סינון, ניתוח והשוואה בלבד. השירות אינו מהווה ייעוץ השקעות, שיווק השקעות, ניהול תיקי השקעות, ייעוץ מס, ייעוץ משפטי, ייעוץ חשבונאי או המלצה אישית לבצע פעולה כלשהי בנייר ערך, במדד, בקרן, באופציה או בכל נכס פיננסי אחר.

כל מידע, גרף, דירוג, ציון, יחס פיננסי, תוצאה ממודל, הערכת שווי, מסקנה אנליטית, סימון איכותי, רשימת מעקב או תוכן אחר בשירות הוא מידע כללי בלבד, שאינו מותאם לנסיבותיו האישיות של המנוי, לצרכיו, ליעדיו, ליכולת הסיכון שלו או למצבו הפיננסי. המנוי מצהיר כי הוא מבין שהחלטות השקעה כרוכות בסיכון, לרבות אובדן מלא או חלקי של ההשקעה, וכי כל הסתמכות על השירות נעשית באחריותו הבלעדית.

דיוק הנתונים, עדכניותם והסתמכות על צדדים שלישיים

המפעיל עושה מאמצים סבירים לאסוף, לעבד ולהציג נתונים ומידע ממקורות שנחזים להיות אמינים, לרבות מקורות צד שלישי, אך אינו מתחייב ואינו מציג מצג כי כל נתון, חישוב, גרף, טבלה, נוסחה, שיעור צמיחה, תחזית, הערכת אנליסטים, מכפיל, תשואה, או תוצר אחר הכלול בשירות הוא נכון, מדויק, מלא, עדכני, רציף, מאומת, סופי או נקי משגיאות.

המנוי מאשר כי חלק מן הנתונים עשוי להיות מבוסס על מקורות חיצוניים, על מטבעות שונים, על נתונים משוערים, על נתונים שתוקנו בדיעבד, על כללי המרה, עיגול, סיווג או נרמול, ועל הנחות חישוביות שאינן אחידות בין חברות, בורסות, תקופות או מקורות מידע.

המפעיל רשאי, אך אינו חייב, לתקן, להסיר, לעדכן, להשלים או לשנות נתונים, מתודולוגיות, תצוגות, נוסחאות, פילטרים, מדדים, סימונים ותכונות שירות בכל עת.

נתונים שאינם בזמן אמת

המנוי מאשר כי לא כל הנתונים בשירות ניתנים בזמן אמת. נתונים מסוימים עשויים להיות מושהים, בפיגור, מעובדים בדיעבד, מבוססי סוף יום, סוף רבעון, סוף שנה, או מעודכנים במחזורי זמן שונים בהתאם לסוג הנתון, מקורו, מגבלות רישוי, מגבלות טכניות או מדיניות המפעיל.

גם מקום שבו מוצג נתון כ"עדכני", "יומי", "שוטף", "אחרון" או ניסוח דומה, אין בכך התחייבות לזמן אמת מוחלט, לעדכון מיידי, או להעדר פער מול המקור הרשמי בבורסה, אצל ספק הנתונים או בדיווח תאגידי.

באגים, תקלות, זמינות ותחזוקה

השירות ניתן במתכונת "כמות שהוא" ו"כפי שהוא זמין". המפעיל אינו מתחייב שהשירות יפעל ללא הפרעות, השבתות, תקלות, עומסים, עיכובים, פערי סנכרון, אובדן נתונים, תקלות תצוגה, שיבושי API, באגים, שגיאות חישוב, חדירות אבטחה, נוזקות או כשלים אחרים.

המפעיל יהיה רשאי לבצע תחזוקה, שדרוגים, שינויי ממשק, שינויי תוכן, החלפת ספקי נתונים, הפסקת פיצ'רים, או השבתה זמנית או קבועה של כל חלק מהשירות, כמו גם השבתת השירות כולו, לפי שיקול דעתו הבלעדי.

הסתייגות מאחריות והגבלת אחריות

המפעיל, בעלי מניותיו, מנהליו, עובדיו, יועציו, ספקיו, מעניקי הרישיון שלו, שותפיו העסקיים וספקי הנתונים לא יישאו באחריות לכל נזק, הפסד, הוצאה, אובדן רווח, אובדן הכנסה, אובדן הזדמנות, הפסד השקעה, ירידת ערך, אובדן נתונים, פגיעה במוניטין, או כל נזק ישיר, עקיף, מיוחד, תוצאתי, עונשי או אגבי, הנובע מן השירות, מהשימוש בו, מהיעדר אפשרות להשתמש בו, מהסתמכות עליו, מטעויות בנתונים, מבאגים, מאיחור נתונים, מהשבתה, משינוי מתודולוגיה, או מפעולה שביצע המנוי על סמך השירות.

מבלי לגרוע מן האמור, אחריותו המצטברת המרבית של המפעיל, ככל שתיקבע אחריות כזו על פי דין חל שאינו ניתן להתניה, לא תעלה על הסכום ששילם המנוי למפעיל עבור שלושת החודשים שקדמו לאירוע הנטען, או 500 ש"ח - לפי הנמוך.

שום דבר בהסכם זה לא יפורש כגורע מזכות שלא ניתן להתנות עליה לפי דין קוגנטי.

התחייבויות המנוי ושימוש אסור

המנוי מתחייב להשתמש בשירות לשימושו האישי והפנימי בלבד, בהתאם לדין ובהתאם להסכם זה.

חל איסור מוחלט על המנוי:

(א) לשתף שם משתמש, סיסמה, קוד גישה, קישור גישה או כל אמצעי אימות אחר עם אדם או גוף אחר;
(ב) לאפשר למי שאינו המנוי להשתמש בחשבון, בין בתמורה ובין ללא תמורה;
(ג) למסור, לפרסם, להעביר, למכור, להעניק רישיון משנה, להפיץ, לשכפל, להעלות לרשתות חברתיות, לקבוצות וואטסאפ, לטלגרם, לפורומים, לניוזלטרים, ליוטיוב, לקורסים, ללקוחות, לתלמידים, לצופים או לצדדים שלישיים כל צילום מסך, קובץ ייצוא, טבלה, גרף, ציון, סיכום, רשימת מניות, הערכת שווי, תבנית, מסקנה או נתון שמקורם בשירות, אלא אם התקבל אישור מראש ובכתב מהמפעיל;
(ד) לבצע scraping, crawling, harvesting, data mining, robot access, reverse engineering או כל איסוף אוטומטי של נתונים או תוכן;
(ה) להשתמש בשירות כדי לבנות מוצר מתחרה, בסיס נתונים, אותות מסחר, מערכת המלצות, ניוזלטר בתשלום, שירות לקוחות, בוט, API לא מורשה, או כל שירות מסחרי או חצי-מסחרי אחר;
(ו) לעקוף מגבלות טכניות, רישיוניות או חוזיות של השירות.

קניין רוחני

כל הזכויות בשירות, לרבות זכויות היוצרים, זכויות בסיסי הנתונים, זכויות במבנה הנתונים, בעיצוב, בממשק, בקוד, במודלים, במתודולוגיות, בנוסחאות, בתבניות, בגרפים, בטבלאות, באיורים, במצגות, בסימנים מסחריים, בשם המסחרי, בפיצ'רים, בתיעוד, במידע הטכני שהמנוי הזין לשירות, ובכל תוכן אחר - שייכות למפעיל ו/או לספקיו ו/או למעניקי הרישיון שלו בלבד.

אין בהסכם זה כדי להקנות למנוי כל זכות בעלות או רישיון כלשהו, למעט זכות מוגבלת, אישית, חוזרת וניתנת לביטול להשתמש בשירות בהתאם להסכם זה ובתקופת המנוי בלבד.

פרטיות ושימוש במידע

המפעיל רשאי לאסוף, לעבד ולשמור מידע שמוסר המנוי וכן מידע טכני ומידע שימוש, לרבות פרטי הרשמה, פרטי התקשרות, היסטוריית התחברות, כתובות IP, סוגי מכשירים, קבצי עוגיות, פעולות בתוך המערכת, שימוש בפיצ'רים, פניות תמיכה, נתוני פורטפוליו, רשימות מעקב ונתונים שהמנוי מזין לשירות, והכל לצורך אספקת השירות, אבטחתו, תפעולו, מניעת הונאה, תמיכה, שיפור המוצר, פיתוח פיצ'רים, אכיפת ההסכם, עמידה בדרישות דין ושליחת מסרים תפעוליים.

המפעיל יהיה רשאי, לפי שיקול דעתו הבלעדי, לשלוח למנוי הודעות והתראות לאימייל או לטלפון שסופקו על ידי המנוי, וכל זאת כחלק מהשירות.

המפעיל ינקוט אמצעים סבירים להגנת המידע, אך אינו מתחייב לחסינות מוחלטת מפני חדירה, גישה בלתי מורשית, פגיעה, שיבוש, אובדן או דליפה.

תשלום, חידוש, ביטול והחזר

דמי המנוי, מועדי החיוב, סוג המסלול, תקופת הניסיון אם קיימת, מדיניות החידוש האוטומטי ומדיניות ההחזר יפורטו במסמך נלווה.

אלא אם צוין אחרת במפורש, המנוי מתחדש אוטומטית בתום כל תקופה.

ביטול והחזרים, ככל שיותרו, יהיו בהתאם למדיניות המפעיל כפי שתהא בתוקף במועד הרכישה, ובכל מקרה בכפוף להוראות דין קוגנטי החל על עסקאות מכר מרחוק, שירותים דיגיטליים ושירותי מנוי.

השעיה, סיום וחסימה

המפעיל יהיה רשאי להשעות, להגביל, לחסום או לסיים את המנוי, באופן מיידי וללא הודעה מוקדמת, אם סבר כי המנוי הפר את ההסכם, שיתף חשבון, הפיץ תכנים, ביצע שימוש אסור, פגע במערכת, חשף את המפעיל לסיכון רגולטורי, משפטי, טכנולוגי או מסחרי, או פעל בניגוד לדין.

במקרה של סיום כאמור, המפעיל רשאי למחוק, להגביל או לנעול גישה לכל תוכן או מידע שנשמר בחשבון, בכפוף לדין.

שיפוי

המנוי ישפה ויפצה את המפעיל, עובדיו, מנהליו, ספקיו ומעניקי הרישיון שלו בגין כל תביעה, דרישה, נזק, הפסד, עלות והוצאה, לרבות שכר טרחת עורכי דין סביר, הנובעים מהפרת ההסכם, מהפרת דין, משיתוף לא מורשה, מהפצת תכנים, מהעלאת תוכן מפר, או מכל שימוש שלו בשירות.

שינויים בהסכם ובשירות

המפעיל רשאי לעדכן מעת לעת את תנאי ההסכם, את היקף השירות, את הפיצ'רים, את ספקי הנתונים, את התמחור ואת מדיניות המוצר. שינויים מהותיים ייכנסו לתוקף במועד שיפורסם באתר או שיימסר למנוי בדרך סבירה אחרת. המשך שימוש בשירות לאחר מועד התחילה יהווה הסכמה לנוסח המעודכן, בכפוף לדין החל.

דין חל וסמכות שיפוט

על הסכם זה יחולו דיני מדינת ישראל בלבד. סמכות השיפוט המקומית הבלעדית בכל מחלוקת הנוגעת להסכם זה, לשירות או לשימוש בו תהא לבתי המשפט המוסמכים במחוז תל אביב – יפו, אלא אם דין קוגנטי מחייב אחרת.

כללי

אם ייקבע שחלק מהוראות ההסכם אינו אכיף או בטל, לא יהיה בכך כדי לפגוע ביתר הוראות ההסכם, אשר יישארו בתוקפן המלא.

אי-אכיפה של זכות כלשהי לפי ההסכם לא תיחשב ויתור עליה.`;

const App: React.FC = () => {
  const AdvancedChartScreenComponent =
    ((AdvancedChartScreen as unknown as { default?: React.ComponentType<any> })?.default) ||
    AdvancedChartScreen;

  const startupEulaBoldTerms = useMemo(
    () => [
      'הסכם מנוי, תנאי שימוש והסתייגות מאחריות',
      'המפעיל',
      'המנוי',
      'הקדמה והסכמה',
      'הגדרות השירות',
      'אופי השירות והיעדר ייעוץ השקעות',
      'דיוק הנתונים, עדכניותם והסתמכות על צדדים שלישיים',
      'נתונים שאינם בזמן אמת',
      'באגים, תקלות, זמינות ותחזוקה',
      'הסתייגות מאחריות והגבלת אחריות',
      'התחייבויות המנוי ושימוש אסור',
      'קניין רוחני',
      'פרטיות ושימוש במידע',
      'תשלום, חידוש, ביטול והחזר',
      'השעיה, סיום וחסימה',
      'שיפוי',
      'שינויים בהסכם ובשירות',
      'דין חל וסמכות שיפוט',
      'כללי',
    ],
    []
  );

  const startupEulaHtml = useMemo(() => {
    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    let html = escapeHtml(STARTUP_EULA_HEBREW_TEXT);
    startupEulaBoldTerms.forEach((term) => {
      const pattern = new RegExp(escapeRegExp(term), 'g');
      html = html.replace(pattern, `<strong>${escapeHtml(term)}</strong>`);
    });

    return html.replace(/\n/g, '<br />');
  }, [startupEulaBoldTerms]);

  const AUTH_STORAGE_KEY = 'garp_admin_auth_v1';
  const LAST_LOGIN_EMAIL_KEY = 'garp_last_login_email_v1';
  const LAST_SELECTED_STOCK_KEY = 'garp_last_selected_stock_v1';
  const LAST_ACTIVE_PAGE_KEY = 'garp_last_active_page_v1';
  const LAST_ACTIVE_TAB_KEY = 'garp_last_active_tab_v1';
  const BACK_TESTING_SYNC_FROM_CHARTS_KEY = 'garp_backtesting_sync_from_charts_v1';
  const SESSION_IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000;
  const SESSION_ACTIVITY_PERSIST_THROTTLE_MS = 60 * 1000;
  const SESSION_KEEP_ALIVE_INTERVAL_MS = 10 * 60 * 1000;

  type AuthState = {
    token: string;
    expiresAtUtc: string;
    username: string;
    userId?: number;
    isEulaAccepted?: boolean;
    eulaPdfLink?: string | null;
    email?: string | null;
  };

  const isAdminRoute = useMemo(() => {
    try {
      const path = window.location.pathname.toLowerCase();
      return path.includes('/administrator') || path.endsWith('administrator');
    } catch {
      return false;
    }
  }, []);

  const isVInvestAIEnabled = process.env.REACT_APP_ENABLE_VINVESTAI === 'true';
  const defaultSymbol = config.api?.defaultSymbol || 'AAPL';
  const initialStartDate = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000);
  const initialEndDate = new Date();

  const [currentPage, setCurrentPage] = useState<string>(() => {
    try {
      const raw = localStorage.getItem(LAST_ACTIVE_PAGE_KEY);
      const page = (raw || '').trim();
      return page || 'Research';
    } catch {
      return 'Research';
    }
  });

  const [quickAddWatchlistOpen, setQuickAddWatchlistOpen] = useState(false);
  const [quickAddWatchlistSymbol, setQuickAddWatchlistSymbol] = useState<string | null>(null);

  const [quickAddPortfolioHoldingOpen, setQuickAddPortfolioHoldingOpen] = useState(false);
  const [quickAddPortfolioHoldingSymbol, setQuickAddPortfolioHoldingSymbol] = useState<string | null>(null);

  const PORTFOLIO_ADD_UNDO_MS = 6500;
  type PendingPortfolioHoldingAdd = { holdingId: number; portfolioName: string; timerId: number };
  const [pendingPortfolioHoldingAdd, setPendingPortfolioHoldingAdd] = useState<PendingPortfolioHoldingAdd | null>(null);
  const pendingPortfolioHoldingAddRef = useRef<PendingPortfolioHoldingAdd | null>(null);

  useEffect(() => {
    pendingPortfolioHoldingAddRef.current = pendingPortfolioHoldingAdd;
  }, [pendingPortfolioHoldingAdd]);

  useEffect(() => {
    if (!isVInvestAIEnabled && currentPage === 'VInvestAI') {
      setCurrentPage('Research');
    }
  }, [currentPage, isVInvestAIEnabled]);

  useEffect(() => {
    try {
      const page = (currentPage || '').trim();
      if (page) localStorage.setItem(LAST_ACTIVE_PAGE_KEY, page);
    } catch {
      // ignore
    }
  }, [currentPage]);

  const normalizeResearchTab = useCallback((tab: string): string => {
    const value = (tab || '').trim();
    if (!value) return 'Summary';
    if (value === 'Chart fpm') return 'Charts';
    return value;
  }, []);

  const [activeTab, setActiveTab] = useState<string>(() => {
    try {
      const raw = localStorage.getItem(LAST_ACTIVE_TAB_KEY);
      const tab = (raw || '').trim();
      return tab === 'Chart fpm' ? 'Charts' : (tab || 'Summary');
    } catch {
      return 'Summary';
    }
  });
  const [balanceSheetPeriod, setBalanceSheetPeriod] = useState<'annual' | 'quarter'>('annual');
  const [backTestingFinancialsSource, setBackTestingFinancialsSource] = useState<'fmp' | 'fiscal'>('fiscal');

  useEffect(() => {
    try {
      const tab = (activeTab || '').trim();
      if (tab) localStorage.setItem(LAST_ACTIVE_TAB_KEY, tab);
    } catch {
      // ignore
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'Growth Fiscal') {
      setActiveTab('Growth');
      return;
    }
    if (activeTab === 'Chart fpm') {
      setActiveTab('Charts');
    }
  }, [activeTab]);

  const handleResearchTabChange = useCallback((tab: string) => {
    const normalizedTab = normalizeResearchTab(tab);
    try {
      if ((activeTab === 'Charts' || activeTab === 'Chart fpm') && normalizedTab === 'Back Testing') {
        window.sessionStorage.setItem(BACK_TESTING_SYNC_FROM_CHARTS_KEY, '1');
      } else if (normalizedTab !== 'Back Testing') {
        window.sessionStorage.removeItem(BACK_TESTING_SYNC_FROM_CHARTS_KEY);
      }
    } catch {
      // ignore
    }

    if (normalizedTab === 'Back Testing') {
      if (activeTab === 'Chart fpm') {
        setBackTestingFinancialsSource('fmp');
      } else {
        setBackTestingFinancialsSource('fiscal');
      }
    }

    setActiveTab(normalizedTab);
  }, [activeTab, normalizeResearchTab]);

  const [auth, setAuth] = useState<AuthState | null>(() => {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as AuthState;
      if (!parsed?.token || !parsed?.expiresAtUtc || !parsed?.username) return null;
      const expiresMs = new Date(parsed.expiresAtUtc).getTime();
      if (!Number.isFinite(expiresMs) || expiresMs <= Date.now()) {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        return null;
      }
      if (typeof parsed.isEulaAccepted !== 'boolean') {
        parsed.isEulaAccepted = false;
      }
      return parsed;
    } catch {
      return null;
    }
  });

  const [savedStocks, setSavedStocks] = useState<string[]>([]);
  const [savedStocksError, setSavedStocksError] = useState<string | null>(null);
  const lastActivityAtMsRef = useRef<number>(Date.now());
  const lastSessionPersistAtMsRef = useRef<number>(0);
  const lastKeepAliveAtMsRef = useRef<number>(0);
  const keepAliveInFlightRef = useRef<boolean>(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [isAdminGroup, setIsAdminGroup] = useState(false);
  const loginSeedUsers = useMemo(() => ['admin', 'alonh', 'shaimuli'], []);
  const [loginUsers, setLoginUsers] = useState<string[]>(loginSeedUsers);
  const [loginUser, setLoginUser] = useState<string>(loginSeedUsers[0]);
  const [loginEmail, setLoginEmail] = useState('');
  const [hasLoginEmailInteracted, setHasLoginEmailInteracted] = useState(false);

  useEffect(() => {
    const next = String(loginEmail || '').trim();
    if (!next || !next.includes('@')) return;
    try {
      localStorage.setItem(LAST_LOGIN_EMAIL_KEY, next);
    } catch {
      // ignore
    }
  }, [LAST_LOGIN_EMAIL_KEY, loginEmail]);

  const [isLoginEmailFocused, setIsLoginEmailFocused] = useState(false);
  const [loginEmailLookupError, setLoginEmailLookupError] = useState<string | null>(null);
  const [isLoginEmailLookupBusy, setIsLoginEmailLookupBusy] = useState(false);
  const [isStartupVerificationReady, setIsStartupVerificationReady] = useState(false);
  const [loginPhoneLast3, setLoginPhoneLast3] = useState('');
  const [loginPhoneNumber, setLoginPhoneNumber] = useState('');
  const [loginVerificationCodeError, setLoginVerificationCodeError] = useState<string | null>(null);
  const loginEmailInputRef = useRef<HTMLInputElement | null>(null);
  const loginPhoneInputRef = useRef<HTMLInputElement | null>(null);
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [loginPopupError, setLoginPopupError] = useState<string | null>(null);
  const [isStartupSigninSectionUnlocked, setIsStartupSigninSectionUnlocked] = useState(false);
  const startupSigninUnlockBufferRef = useRef('');
  const [isUlaModalOpen, setIsUlaModalOpen] = useState(false);
  const [isUlaAccepted, setIsUlaAccepted] = useState(false);
  const [isUlaConsentChecked, setIsUlaConsentChecked] = useState(false);
  const [isUlaScrolledToBottom, setIsUlaScrolledToBottom] = useState(false);
  const [ulaFirstName, setUlaFirstName] = useState('');
  const [ulaLastName, setUlaLastName] = useState('');
  const [ulaNationalId, setUlaNationalId] = useState('');
  const [hasUlaSignature, setHasUlaSignature] = useState(false);
  const [isUlaSavingPdf, setIsUlaSavingPdf] = useState(false);
  const [ulaSaveError, setUlaSaveError] = useState<string | null>(null);
  const ulaSignatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const ulaSignatureDrawingRef = useRef(false);

  const isEulaGateActive = Boolean(auth?.token) && isUlaModalOpen && !Boolean(auth?.isEulaAccepted);

  const handlePageChange = useCallback((page: string) => {
    if (isEulaGateActive) return;
    setCurrentPage(page);
  }, [isEulaGateActive]);

  const isAdminToken = useCallback((token?: string | null): boolean => {
    try {
      if (!token) return false;
      const parts = token.split('.');
      if (parts.length < 2) return false;
      const payload = parts[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const json = atob(padded);
      const data = JSON.parse(json);

      const roleValuesRaw = [
        data?.role,
        data?.roles,
        data?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
        data?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/role'],
      ];

      const roleValues = roleValuesRaw
        .flatMap((v) => (Array.isArray(v) ? v : [v]))
        .map((v) => String(v ?? '').trim().toLowerCase())
        .filter((v) => v.length > 0);

      return roleValues.includes('admin');
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    let disposed = false;

    adminApi
      .listPublicUsernames(auth?.token)
      .then((list) => {
        if (disposed) return;
        const cleaned = (list || [])
          .map((u) => String(u || '').trim())
          .filter((u) => u.length > 0);
        const unique = Array.from(new Set([...loginSeedUsers, ...cleaned]));
        if (unique.length === 0) return;
        setLoginUsers(unique);
        setLoginUser((prev) => (unique.includes(prev) ? prev : unique[0]));
      })
      .catch(() => {
        // Keep seeded users when the public users endpoint is unavailable.
      });
    return () => {
      disposed = true;
    };
  }, [auth?.token, loginSeedUsers]);

  useEffect(() => {
    if (auth || isStartupSigninSectionUnlocked) return;

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.ctrlKey || ev.altKey || ev.metaKey) return;
      const key = (ev.key || '').toLowerCase();
      if (key.length !== 1 || key < 'a' || key > 'z') return;

      const next = (startupSigninUnlockBufferRef.current + key).slice(-7);
      startupSigninUnlockBufferRef.current = next;
      if (next === 'logmein') {
        const currentEmail = (loginEmailInputRef.current?.value ?? '').trim().toLowerCase();
        if (currentEmail === 'asvi@garp.com') {
          setIsStartupSigninSectionUnlocked(true);
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [auth, isStartupSigninSectionUnlocked]);

  useEffect(() => {
    if (!auth) {
      setLoginEmail('');
      setIsLoginEmailFocused(false);
      setHasLoginEmailInteracted(false);
      setIsStartupVerificationReady(false);
      setLoginPhoneLast3('');
      setLoginPhoneNumber('');
      setIsStartupSigninSectionUnlocked(false);
      setIsUlaModalOpen(false);
      setIsUlaAccepted(false);
      setIsUlaConsentChecked(false);
      setIsUlaScrolledToBottom(false);
      setUlaFirstName('');
      setUlaLastName('');
      setUlaNationalId('');
      setHasUlaSignature(false);
      setIsUlaSavingPdf(false);
      setUlaSaveError(null);
      startupSigninUnlockBufferRef.current = '';
      lastActivityAtMsRef.current = Date.now();
      lastSessionPersistAtMsRef.current = 0;
      lastKeepAliveAtMsRef.current = 0;
      keepAliveInFlightRef.current = false;
    }
  }, [auth]);

  useEffect(() => {
    if (!auth) return;
    const adminSession = isAdminToken(auth.token);
    setIsUlaModalOpen(!adminSession && !Boolean(auth.isEulaAccepted));
  }, [auth?.token, auth?.isEulaAccepted, isAdminToken]);

  useEffect(() => {
    if (!auth?.token) return;

    let disposed = false;

    const sendKeepAlive = async () => {
      const now = Date.now();
      if (keepAliveInFlightRef.current) return;
      if (now - lastKeepAliveAtMsRef.current < SESSION_KEEP_ALIVE_INTERVAL_MS) return;

      keepAliveInFlightRef.current = true;
      try {
        // Refresh JWT during active usage so session expires only on inactivity.
        const refreshed = await adminApi.refresh(auth.token);
        if (disposed) return;
        lastKeepAliveAtMsRef.current = Date.now();
        setAuth((prev) => {
          if (!prev || prev.token !== auth.token) return prev;
          return {
            ...prev,
            token: refreshed.token,
            expiresAtUtc: refreshed.expiresAtUtc,
            username: refreshed.username || prev.username,
            isEulaAccepted: Boolean(refreshed.isEulaAccepted ?? prev.isEulaAccepted),
            eulaPdfLink: refreshed.eulaPdfLink ?? prev.eulaPdfLink ?? null,
            email: refreshed.email ?? prev.email ?? null,
          };
        });
      } catch (e: any) {
        if (disposed) return;
        if (isUnauthorized(e)) {
          setSavedStocksError('Session expired. Please Connect again.');
          setAuth(null);
        }
      } finally {
        keepAliveInFlightRef.current = false;
      }
    };

    const markActivity = () => {
      const now = Date.now();
      lastActivityAtMsRef.current = now;

      // Keep local auth expiry aligned to an inactivity window.
      // Throttle writes to avoid rerendering on every mouse move.
      if (now - lastSessionPersistAtMsRef.current < SESSION_ACTIVITY_PERSIST_THROTTLE_MS) return;
      lastSessionPersistAtMsRef.current = now;

      setAuth((prev) => {
        if (!prev || prev.token !== auth.token) return prev;
        const nextExpiresAtUtc = new Date(now + SESSION_IDLE_TIMEOUT_MS).toISOString();
        if (prev.expiresAtUtc === nextExpiresAtUtc) return prev;
        return { ...prev, expiresAtUtc: nextExpiresAtUtc };
      });

      void sendKeepAlive();
    };

    markActivity();

    const activityEvents: Array<keyof WindowEventMap> = [
      'pointerdown',
      'keydown',
      'mousemove',
      'scroll',
      'touchstart',
      'focus',
    ];

    const onActivity = () => {
      markActivity();
    };

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });

    const idleTimer = window.setInterval(() => {
      const idleMs = Date.now() - lastActivityAtMsRef.current;
      if (idleMs < SESSION_IDLE_TIMEOUT_MS) return;
      setSavedStocksError('Session expired after 2 hours of inactivity. Please Connect again.');
      setAuth(null);
    }, 30_000);

    const keepAliveTimer = window.setInterval(() => {
      const idleMs = Date.now() - lastActivityAtMsRef.current;
      if (idleMs > SESSION_KEEP_ALIVE_INTERVAL_MS) return;
      void sendKeepAlive();
    }, 60_000);

    return () => {
      disposed = true;
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
      window.clearInterval(idleTimer);
      window.clearInterval(keepAliveTimer);
    };
  }, [auth?.token]);



  const handleUnauthorized = useCallback(() => {
    setAuth(null);
    setSavedStocks([]);
    setSavedStocksError('Session expired. Please Connect again.');
    stockHistoryCutoffRef.current = 0;
  }, []);

  const snackbarTimerRef = useRef<number | undefined>(undefined);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; variant: SnackbarVariant }>(() => ({
    open: false,
    message: '',
    variant: 'info',
  }));
  const snackbarRef = useRef(snackbar);

  useEffect(() => {
    snackbarRef.current = snackbar;
  }, [snackbar]);

  const closeSnackbar = useCallback(() => {
    if (snackbarTimerRef.current) {
      window.clearTimeout(snackbarTimerRef.current);
      snackbarTimerRef.current = undefined;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const showSnackbar = useCallback((message: string, variant: SnackbarVariant = 'info') => {
    if (!config.ui?.showStatusSnackbar) return;
    if (!message || !message.trim()) {
      closeSnackbar();
      return;
    }

    const current = snackbarRef.current;
    const isSame = current.open && current.message === message && current.variant === variant;

    // If autosave or other events keep firing with the same payload,
    // don't keep extending the snackbar lifetime.
    if (isSame) return;

    if (snackbarTimerRef.current) {
      window.clearTimeout(snackbarTimerRef.current);
      snackbarTimerRef.current = undefined;
    }

    setSnackbar({ open: true, message, variant });
    snackbarTimerRef.current = window.setTimeout(() => {
      setSnackbar((prev) => ({ ...prev, open: false }));
      snackbarTimerRef.current = undefined;
    }, 10_000);
  }, [closeSnackbar]);

  const currentActivityLabel = useMemo(() => {
    if (currentPage === 'Research') return activeTab;
    return currentPage;
  }, [activeTab, currentPage]);

  // Tracks the latest StockHistory timestamp we've incorporated into the visible chips.
  // This prevents older (previously hidden) symbols from "backfilling" after a deletion.
  const stockHistoryCutoffRef = React.useRef<number>(0);

  useEffect(() => {
    try {
      if (auth) localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
      else localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
  }, [auth]);

  const ensureUserId = async (current: AuthState): Promise<number | null> => {
    if (typeof current.userId === 'number' && Number.isFinite(current.userId)) return current.userId;

    const tryParseUserIdFromToken = (token: string): number | null => {
      if (!token) return null;
      const parts = token.split('.');
      if (parts.length < 2) return null;
      try {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        const json = atob(padded);
        const payload = JSON.parse(json) as Record<string, unknown>;
        const raw = payload.userId ?? payload.userid ?? payload.id ?? payload.sub ?? payload.nameid;
        if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
        if (typeof raw === 'string') {
          const parsed = Number(raw);
          return Number.isFinite(parsed) ? parsed : null;
        }
        return null;
      } catch {
        return null;
      }
    };

    const tokenUserId = tryParseUserIdFromToken(current.token);
    if (typeof tokenUserId === 'number' && Number.isFinite(tokenUserId)) {
      setAuth(prev => {
        if (!prev) return prev;
        const updated: AuthState = { ...prev, userId: tokenUserId };
        return updated;
      });
      return tokenUserId;
    }

    if ((current.username || '').toLowerCase() !== 'admin') {
      return null;
    }

    try {
      const users = await adminApi.listUsers(current.token);
      const match = users.find(u => (u.username || '').toLowerCase() === current.username.toLowerCase());
      if (!match) {
        throw new Error(`User '${current.username}' not found`);
      }
      setAuth(prev => {
        if (!prev) return prev;
        const updated: AuthState = { ...prev, userId: match.id };
        return updated;
      });
      return match.id;
    } catch (e: any) {
      if (typeof e?.status === 'number' && e.status === 403) {
        return null;
      }
      throw e;
    }
  };

  const refreshSavedStocks = async (current: AuthState) => {
    setSavedStocksError(null);
    try {
      const history = await adminApi.listMyStockHistory(current.token);
      const items = history
        .slice()
        // Stable chip order: newest -> oldest (left-most is newest).
        .sort((a, b) => {
          const ta = new Date(a.createdAtUtc).getTime();
          const tb = new Date(b.createdAtUtc).getTime();
          if (Number.isFinite(tb) && Number.isFinite(ta) && tb !== ta) return tb - ta;
          const ida = typeof a.id === 'number' ? a.id : 0;
          const idb = typeof b.id === 'number' ? b.id : 0;
          return idb - ida;
        });

      const maxCreatedAtMs = items.reduce((acc, it) => {
        const t = new Date(it.createdAtUtc).getTime();
        if (!Number.isFinite(t)) return acc;
        return Math.max(acc, t);
      }, 0);

      const uniqueNewestFirst: string[] = [];
      const seen = new Set<string>();
      for (const it of items) {
        const sym = (it.stockName || '').trim().toUpperCase();
        if (!sym) continue;
        if (seen.has(sym)) continue;
        seen.add(sym);
        uniqueNewestFirst.push(sym);
      }

      const last8Unique = uniqueNewestFirst.length <= 8 ? uniqueNewestFirst : uniqueNewestFirst.slice(0, 8);
      const fullSet = new Set<string>(uniqueNewestFirst);
      const cutoff = stockHistoryCutoffRef.current || 0;

      const newlyAddedSymbols: string[] = [];
      const newlySeen = new Set<string>();
      for (const it of items) {
        const t = new Date(it.createdAtUtc).getTime();
        if (!Number.isFinite(t) || t <= cutoff) continue;
        const sym = (it.stockName || '').trim().toUpperCase();
        if (!sym) continue;
        if (newlySeen.has(sym)) continue;
        newlySeen.add(sym);
        newlyAddedSymbols.push(sym);
      }

      setSavedStocks(prev => {
        // First load: show newest -> oldest (left-most newest).
        if (!prev || prev.length === 0) return last8Unique;

        // Subsequent refresh: shrink on deletions, and keep newest symbols on the left.
        const kept = prev.filter(sym => fullSet.has(sym));
        const merged = [...newlyAddedSymbols, ...kept];
        const next: string[] = [];
        const seenNext = new Set<string>();
        for (const sym of merged) {
          if (seenNext.has(sym)) continue;
          seenNext.add(sym);
          next.push(sym);
          if (next.length >= 8) break;
        }
        return next;
      });

      if (maxCreatedAtMs > 0) {
        stockHistoryCutoffRef.current = maxCreatedAtMs;
      }
    } catch (e: any) {
      if (isUnauthorized(e)) {
        handleUnauthorized();
        return;
      }

      setSavedStocks([]);
      setSavedStocksError(e?.message || 'Failed to load saved stocks');
    }
  };

  // Refresh saved stocks whenever auth becomes available
  useEffect(() => {
    if (!auth) {
      setSavedStocks([]);
      setSavedStocksError(null);
      stockHistoryCutoffRef.current = 0;
      return;
    }

    const expiresMs = new Date(auth.expiresAtUtc).getTime();
    if (!Number.isFinite(expiresMs) || expiresMs <= Date.now()) {
      setAuth(null);
      return;
    }

    refreshSavedStocks(auth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.token]);

  useEffect(() => {
    if (!auth?.token) {
      setIsAdminGroup(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const users = await adminApi.listUsers(auth.token);
        if (cancelled) return;
        const byId = typeof auth.userId === 'number'
          ? users.find((u) => u.id === auth.userId)
          : null;
        const byName = !byId
          ? users.find((u) => (u.username || '').toLowerCase() === (auth.username || '').toLowerCase())
          : null;
        const current = byId ?? byName;
        const group = (current?.userGroup ?? '').toString().trim().toLowerCase();
        setIsAdminGroup(group === 'admin');
      } catch (e: any) {
        if (cancelled) return;
        if (isUnauthorized(e) || e?.status === 403) {
          setIsAdminGroup(false);
          return;
        }
        setIsAdminGroup(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [auth?.token, auth?.userId, auth?.username]);

  // Resolve userId early so server-persisted prefs work on first visit.
  useEffect(() => {
    if (!auth) return;
    if (typeof auth.userId === 'number' && Number.isFinite(auth.userId)) return;

    void (async () => {
      try {
        await ensureUserId(auth);
      } catch {
        // ignore; server-backed prefs will retry later
      }
    })();
  }, [auth?.token]);

  // When entering the Research page, default to Summary (instead of preserving e.g. Charts).
  const pendingResearchTabOnEnterRef = React.useRef<string | null>(null);
  const previousPageRef = React.useRef<string>(currentPage);
  useEffect(() => {
    const previousPage = previousPageRef.current;
    previousPageRef.current = currentPage;

    if (currentPage === 'Research' && previousPage !== 'Research') {
      const pending = pendingResearchTabOnEnterRef.current;
      pendingResearchTabOnEnterRef.current = null;
      setActiveTab(normalizeResearchTab(pending ?? 'Summary'));
    }
  }, [currentPage, normalizeResearchTab]);

  // Removed useEffect that prevented navigation to 'Balance Sheet' and 'Income Statement'

  const [state, setState] = useState<AppState>({
    selectedStock: (() => {
      try {
        const raw = localStorage.getItem(LAST_SELECTED_STOCK_KEY);
        const sym = (raw || '').trim().toUpperCase();
        return sym || defaultSymbol;
      } catch {
        return defaultSymbol;
      }
    })(),
    selectedRange: '1Y',
    chartType: 'line',
    theme: 'light',
    currentScreen: 'advanced',
    indicators: {
      showEMA12: true,
      showEMA26: true,
      showEMA50: true,
      showEMA200: true,
      showVolume: true,
    },
    customStartDate: undefined,
    customEndDate: undefined,
    navigatorRange: [initialStartDate, initialEndDate],
  });

  const handleAddToMyWatchlist = useCallback(() => {
    const sym = (state.selectedStock || '').trim().toUpperCase();
    setQuickAddWatchlistSymbol(sym || null);
    setQuickAddWatchlistOpen(true);
  }, [state.selectedStock]);

  const dismissPendingPortfolioHoldingAdd = useCallback(() => {
    const pending = pendingPortfolioHoldingAddRef.current;
    if (pending) window.clearTimeout(pending.timerId);
    pendingPortfolioHoldingAddRef.current = null;
    setPendingPortfolioHoldingAdd(null);
  }, []);

  const handleAddToMyPortfolio = useCallback(() => {
    const sym = String(state.selectedStock || '').trim().toUpperCase();

    if (!auth?.token) {
      showSnackbar('Please login to add holdings to your portfolio.', 'error');
    }

    if (!sym) {
      showSnackbar('Please select a stock first.', 'error');
    }

    setQuickAddPortfolioHoldingSymbol(sym || null);
    setQuickAddPortfolioHoldingOpen(true);
  }, [auth?.token, showSnackbar, state.selectedStock]);

  const handleCloseQuickAddWatchlist = useCallback(() => {
    setQuickAddWatchlistOpen(false);
  }, []);

  const handleCloseQuickAddPortfolioHolding = useCallback(() => {
    setQuickAddPortfolioHoldingOpen(false);
  }, []);

  // API-only price series for the main (non-advanced) chart.
  const [mainPriceData, setMainPriceData] = useState<StockDataPoint[]>([]);
  const lastNavigatorInitializedForSymbol = React.useRef<string | null>(null);

  // Keep a local fallback of the last selected symbol.
  useEffect(() => {
    try {
      const sym = (state.selectedStock || '').trim().toUpperCase();
      if (sym) localStorage.setItem(LAST_SELECTED_STOCK_KEY, sym);
    } catch {
      // ignore
    }
  }, [state.selectedStock]);

  const lastSelectedPrefHydratedRef = useRef(false);

  // If logged in, persist last-selected symbol to DB-backed preferences (best-effort).
  useEffect(() => {
    if (!auth) return;
    const symbol = (state.selectedStock || '').trim().toUpperCase();
    if (!symbol) return;

    // If localStorage is empty (e.g. new browser) and we haven't hydrated from server yet,
    // don't immediately overwrite the server preference with the default symbol.
    let hasLocal = false;
    try {
      const raw = localStorage.getItem(LAST_SELECTED_STOCK_KEY);
      hasLocal = !!(raw && raw.trim());
    } catch {
      hasLocal = false;
    }
    if (!hasLocal && !lastSelectedPrefHydratedRef.current) return;

    (async () => {
      try {
        await adminApi.setLastSelectedStock(auth.token, symbol);
      } catch (e) {
        if (isUnauthorized(e)) handleUnauthorized();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.token, state.selectedStock]);

  // On login, if localStorage doesn't already have a last-selected symbol,
  // try to load it from DB-backed preferences.
  useEffect(() => {
    if (!auth) {
      lastSelectedPrefHydratedRef.current = false;
      return;
    }

    let hasLocal = false;
    try {
      const raw = localStorage.getItem(LAST_SELECTED_STOCK_KEY);
      hasLocal = !!(raw && raw.trim());
    } catch {
      hasLocal = false;
    }
    if (hasLocal) return;

    (async () => {
      try {
        const pref = await adminApi.getLastSelectedStock(auth.token);
        const sym = (pref?.symbol || '').trim().toUpperCase();
        if (!sym) return;

        try {
          localStorage.setItem(LAST_SELECTED_STOCK_KEY, sym);
        } catch {
          // ignore
        }

        setState(prev => ({
          ...prev,
          selectedStock: sym,
        }));
      } catch (e) {
        if (isUnauthorized(e)) handleUnauthorized();
      } finally {
        lastSelectedPrefHydratedRef.current = true;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.token]);

  useEffect(() => {
    let cancelled = false;
    setMainPriceData([]);
    (async () => {
      const prices = await fetchHistoricalPrices(state.selectedStock);
      if (cancelled) return;

      const mapped: StockDataPoint[] = (prices || [])
        .map((p) => {
          const close = typeof p.close === 'number' ? p.close : Number(p.price);
          if (!Number.isFinite(close)) return null;
          const open = (typeof p.open === 'number' && Number.isFinite(p.open)) ? p.open : close;
          const high = (typeof p.high === 'number' && Number.isFinite(p.high)) ? p.high : Math.max(open, close);
          const low = (typeof p.low === 'number' && Number.isFinite(p.low)) ? p.low : Math.min(open, close);
          const volume = (typeof p.volume === 'number' && Number.isFinite(p.volume)) ? p.volume : 0;
          return {
            timestamp: new Date(p.date),
            open,
            high,
            low,
            close,
            volume,
          };
        })
        .filter(Boolean) as StockDataPoint[];

      setMainPriceData(mapped);
    })();
    return () => {
      cancelled = true;
    };
  }, [state.selectedStock]);

  // When a new symbol's data arrives, initialize navigatorRange to the last ~1Y.
  useEffect(() => {
    if (lastNavigatorInitializedForSymbol.current === state.selectedStock) return;
    if (!mainPriceData || mainPriceData.length === 0) return;

    const startIdx = Math.max(0, mainPriceData.length - 365);
    const start = new Date(mainPriceData[startIdx].timestamp);
    const end = new Date(mainPriceData[mainPriceData.length - 1].timestamp);
    lastNavigatorInitializedForSymbol.current = state.selectedStock;

    setState(prev => ({
      ...prev,
      navigatorRange: [start, end],
    }));
  }, [mainPriceData, state.selectedStock]);

  const controlPanelStocks = useMemo(() => {
    // Keep this list short to avoid a huge dropdown.
    const excluded = new Set(['']);
    const preferredSymbols = [defaultSymbol, 'AAPL', 'GOOG', 'MSFT', 'AMZN', 'NVDA', 'META', 'TSLA']
      .filter((v, i, a) => a.indexOf(v) === i)
      .filter((sym) => !excluded.has(sym));
    const list = stocksList as Array<{ symbol: string; name: string }>;
    return preferredSymbols.map((sym) => {
      const meta = list.find((s) => s.symbol === sym);
      return {
        symbol: sym,
        name: meta?.name || sym,
      };
    });
  }, [defaultSymbol]);

  // Get all data for the selected stock
  const allStockData = useMemo((): StockDataPoint[] => {
    return mainPriceData;
  }, [mainPriceData]);

  // Get filtered data based on navigator range
  const filteredData = useMemo((): StockDataPoint[] => {
    return filterDataByDateRange(
      allStockData,
      state.navigatorRange[0],
      state.navigatorRange[1]
    );
  }, [allStockData, state.navigatorRange]);

  // Calculate technical indicators for filtered data
  const technicalIndicators = useMemo(() => {
    return calculateTechnicalIndicators(filteredData);
  }, [filteredData]);

  // Event handlers (removed unused handleStockChange)

  const handleRangeChange = (range: string, startDate: Date, endDate: Date) => {
    setState(prev => ({
      ...prev,
      selectedRange: range,
      customStartDate: range === 'CUSTOM' ? startDate : undefined,
      customEndDate: range === 'CUSTOM' ? endDate : undefined,
    }));
  };

  const handleChartTypeChange = (chartType: 'line' | 'candlestick') => {
    setState(prev => ({ ...prev, chartType }));
  };

  const handleIndicatorChange = (indicator: string, value: boolean) => {
    setState(prev => ({
      ...prev,
      indicators: {
        ...prev.indicators,
        [indicator]: value,
      },
    }));
  };

  const handleThemeChange = (theme: 'light' | 'dark') => {
    setState(prev => ({ ...prev, theme }));
  };

  const handleCustomDateChange = (startDate: Date, endDate: Date) => {
    setState(prev => ({
      ...prev,
      customStartDate: startDate,
      customEndDate: endDate,
    }));
  };

  const handleNavigatorRangeChange = (startDate: Date, endDate: Date) => {
    setState(prev => ({
      ...prev,
      navigatorRange: [startDate, endDate],
    }));
  };

  // Selecting a stock from the Header (search/chips) should keep the current Research tab.
  const handleHeaderSymbolChange = (symbol: string) => {
    const normalized = (symbol || '').trim().toUpperCase();
    if (!normalized) return;
    setMainPriceData([]);
    lastNavigatorInitializedForSymbol.current = null;
    const nextTab = currentPage === 'Research' ? normalizeResearchTab(activeTab) : 'Summary';
    setState(prev => ({
      ...prev,
      selectedStock: normalized,
      // Temporary range until API data arrives.
      navigatorRange: [new Date(Date.now() - 365 * 24 * 60 * 60 * 1000), new Date()],
      currentScreen: 'advanced',
    }));

    pendingResearchTabOnEnterRef.current = nextTab;
    setCurrentPage('Research');
    setActiveTab(nextTab);
  };

  // Selecting a stock from Watchlist/Alerts should keep the current Research tab.
  const handleWatchlistSymbolChange = (symbol: string) => {
    const normalized = (symbol || '').trim().toUpperCase();
    if (!normalized) return;
    setMainPriceData([]);
    lastNavigatorInitializedForSymbol.current = null;
    const nextTab = 'Charts';
    setState((prev) => ({
      ...prev,
      selectedStock: normalized,
      // Temporary range until API data arrives.
      navigatorRange: [new Date(Date.now() - 365 * 24 * 60 * 60 * 1000), new Date()],
      currentScreen: 'advanced',
    }));

    // Keep DB-backed chip history in sync so the Research header can highlight this symbol.
    if (auth) {
      void handleChipAdded(normalized);
    }

    pendingResearchTabOnEnterRef.current = nextTab;
    setCurrentPage('Research');
    setActiveTab(nextTab);
  };

  const handleStockChangeWithNavigator = (symbol: string) => {
    const normalized = (symbol || '').trim().toUpperCase();
    if (!normalized) return;
    setMainPriceData([]);
    lastNavigatorInitializedForSymbol.current = null;
    setState(prev => ({
      ...prev,
      selectedStock: normalized,
      navigatorRange: [new Date(Date.now() - 365 * 24 * 60 * 60 * 1000), new Date()],
    }));

    // Best-effort: log selection in DB so it survives refresh/logout.
    if (auth) {
      (async () => {
        try {
          const userId = await ensureUserId(auth).catch(() => null);
          const username = (auth.username || '').trim();
          const request = userId
            ? { userId, stockName: normalized }
            : username
              ? { username, stockName: normalized }
              : null;
          if (!request) return;
          await adminApi.createStockHistory(auth.token, request);
          await refreshSavedStocks(auth);
        } catch {
          // ignore
        }
      })();
    }
  };

  const navigateToPostLoginPage = useCallback(() => {
    setCurrentPage('Portfolios');
  }, []);

  const handleQuickLogin = async (username: string = 'admin', password: string = 'admin') => {
    if (authBusy) {
      return { ok: false as const, message: 'Login request is already in progress. Please wait a moment.' };
    }
    setAuthBusy(true);
    setSavedStocksError(null);
    try {
      const resp = await adminApi.login(username, password);
      const next: AuthState = {
        token: resp.token,
        expiresAtUtc: resp.expiresAtUtc,
        username: resp.username,
        isEulaAccepted: Boolean(resp.isEulaAccepted),
        eulaPdfLink: resp.eulaPdfLink ?? null,
        email: resp.email ?? null,
      };
      setAuth(next);
      navigateToPostLoginPage();
      // best-effort warm-up
      refreshSavedStocks(next);
      return { ok: true as const, message: null as string | null };
    } catch (e: any) {
      const msg = e?.message || 'Login failed';
      setSavedStocksError(msg);
      return { ok: false as const, message: msg };
    }
    finally {
      setAuthBusy(false);
    }
  };

  const handlePopupLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginPopupError(null);

    const username = String(loginUser || '').trim();
    const password = String(loginPassword || '').trim();

    if (!username) {
      setLoginPopupError('Please select a user.');
      return;
    }
    if (!password) {
      setLoginPopupError('Password is required.');
      return;
    }

    const result = await handleQuickLogin(username, password);
    if (!result?.ok) {
      setLoginPopupError(result?.message || savedStocksError || 'Login failed. Please verify username and password.');
      return;
    }
    setLoginPassword('');
    setShowLoginPassword(false);
  };

  const handleLookupLoginEmail = async () => {
    if (isLoginEmailLookupBusy) return;

    const normalizeEmailValue = (value: string) => value
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/\s+/g, '')
      .trim()
      .toLowerCase();

    const emailFromDom = String(loginEmailInputRef.current?.value ?? '');
    let email = normalizeEmailValue(String(emailFromDom || loginEmail || ''));

    // Some browser autofill modes visually render text before React can read it.
    // Fall back to selected user + gmail so valid built-in users don't false-fail.
    if (!email) {
      const fromSelectedUser = normalizeEmailValue(`${String(loginUser || '').trim()}@gmail.com`);
      if (fromSelectedUser && fromSelectedUser.includes('@')) {
        email = fromSelectedUser;
      }
    }

    if (email !== loginEmail) {
      setLoginEmail(email);
    }
    if (email && email.includes('@')) {
      try {
        localStorage.setItem(LAST_LOGIN_EMAIL_KEY, email);
      } catch {
        // ignore
      }
    }
    if (!email) {
      setLoginEmailLookupError('This email is incorrect/not exist in the system.');
      return;
    }

    setLoginEmailLookupError(null);
    setLoginVerificationCodeError(null);
    setIsLoginEmailLookupBusy(true);
    setIsStartupVerificationReady(false);
    setLoginPhoneLast3('');
    setLoginPhoneNumber('');

    try {
      const result = await adminApi.createStartupLoginVerificationCode(email);
      const exists = Boolean(result.exists);

      if (!exists) {
        setLoginEmailLookupError('This email is incorrect/not exist in the system.');
        return;
      }

      if (!result.hasPhone) {
        setLoginEmailLookupError('No phone number found for this email.');
        return;
      }

      setLoginEmailLookupError(null);
      setLoginPhoneLast3(String(result.phoneLast3 ?? '').trim());
      setIsStartupVerificationReady(true);
      window.setTimeout(() => {
        loginPhoneInputRef.current?.focus();
      }, 0);
    } catch (e: any) {
      const msg = (e?.message || '').toString().trim();
      setLoginEmailLookupError(msg || 'Failed to prepare verification code. Please try again.');
      setIsStartupVerificationReady(false);
    } finally {
      setIsLoginEmailLookupBusy(false);
    }
  };

  const isStartupCodeValid = /^\d{6}$/.test(loginPhoneNumber);
  const canShowStartupUlaButton = String(loginEmail || '').trim().includes('@');
  const isUlaFirstNameValid = String(ulaFirstName || '').trim().length > 0;
  const isUlaLastNameValid = String(ulaLastName || '').trim().length > 0;
  const isUlaNationalIdValid = /^\d{9}$/.test(ulaNationalId);
  const canAcceptUla = isUlaScrolledToBottom && isUlaConsentChecked && isUlaFirstNameValid && isUlaLastNameValid && isUlaNationalIdValid && hasUlaSignature;
  const canEnableStartupNext = isStartupVerificationReady && isStartupCodeValid && !authBusy;

  const drawOnUlaSignatureCanvas = (x: number, y: number) => {
    const canvas = ulaSignatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const beginUlaSignature = (x: number, y: number) => {
    const canvas = ulaSignatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ulaSignatureDrawingRef.current = true;
    setIsUlaAccepted(false);
    if (ulaSaveError) setUlaSaveError(null);
  };

  const endUlaSignature = () => {
    if (!ulaSignatureDrawingRef.current) return;
    ulaSignatureDrawingRef.current = false;
    const canvas = ulaSignatureCanvasRef.current;
    if (!canvas) {
      setHasUlaSignature(false);
      return;
    }
    try {
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setHasUlaSignature(false);
        return;
      }
      const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let hasInk = false;
      for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] > 0) {
          hasInk = true;
          break;
        }
      }
      setHasUlaSignature(hasInk);
    } catch {
      setHasUlaSignature(false);
    }
  };

  const clearUlaSignature = () => {
    const canvas = ulaSignatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasUlaSignature(false);
    setIsUlaAccepted(false);
  };

  const getUlaSignatureDataUrl = () => {
    const canvas = ulaSignatureCanvasRef.current;
    if (!canvas) return '';
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvas.width;
    exportCanvas.height = canvas.height;
    const exportCtx = exportCanvas.getContext('2d');
    if (!exportCtx) return '';
    exportCtx.fillStyle = '#FFFFFF';
    exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    exportCtx.drawImage(canvas, 0, 0);
    return exportCanvas.toDataURL('image/png');
  };

  const handleAcceptUla = async () => {
    if (!canAcceptUla || isUlaSavingPdf) return;
    const hasEmail = String(loginEmail || '').trim().includes('@');

    const signatureDataUrl = getUlaSignatureDataUrl();
    if (!signatureDataUrl) {
      setUlaSaveError('חסרה חתימה דיגיטלית.');
      return;
    }

    if (!auth?.token && !hasEmail) {
      setUlaSaveError('יש להזין אימייל תקין לפני אישור ההסכם.');
      return;
    }

    setIsUlaSavingPdf(true);
    setUlaSaveError(null);
    try {
      await adminApi.createStartupLoginEulaPdf(
        auth?.token ?? null,
        loginEmail,
        ulaNationalId,
        signatureDataUrl,
        ulaFirstName,
        ulaLastName
      );
      setIsUlaAccepted(true);
      setIsUlaModalOpen(false);
      setAuth((prev) => prev ? {
        ...prev,
        isEulaAccepted: true,
      } : prev);
    } catch (e: any) {
      setUlaSaveError(e?.message || 'שמירת מסמך ה-EULA נכשלה.');
    } finally {
      setIsUlaSavingPdf(false);
    }
  };

  const handleStartupVerificationNext = async () => {
    if (!canEnableStartupNext) return;

    setLoginVerificationCodeError(null);
    setSavedStocksError(null);
    setAuthBusy(true);
    try {
      const resp = await adminApi.verifyStartupLoginCode(loginEmail, loginPhoneNumber);
      const next: AuthState = {
        token: resp.token,
        expiresAtUtc: resp.expiresAtUtc,
        username: resp.username,
        isEulaAccepted: Boolean(resp.isEulaAccepted),
        eulaPdfLink: resp.eulaPdfLink ?? null,
        email: resp.email ?? null,
      };
      setAuth(next);
      try {
        const emailToPersist = String(loginEmail || '').trim();
        if (emailToPersist && emailToPersist.includes('@')) {
          localStorage.setItem(LAST_LOGIN_EMAIL_KEY, emailToPersist);
        }
      } catch {
        // ignore
      }
      refreshSavedStocks(next);

      setIsStartupVerificationReady(false);
      setLoginPhoneLast3('');
      setLoginPhoneNumber('');
      setLoginVerificationCodeError(null);
      setLoginEmailLookupError(null);
      setLoginPassword('');
      setShowLoginPassword(false);
      navigateToPostLoginPage();
    } catch (e: any) {
      const msg = e?.message || 'Invalid verification code';
      setLoginVerificationCodeError(msg);
    } finally {
      setAuthBusy(false);
    }
  };

  const renderStartupLoginPopup = () => {
    if (auth && !isUlaModalOpen) return null;

    return (
      <div
        id="startup-login-overlay"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 30000,
          backgroundImage: auth
            ? 'none'
            : "linear-gradient(rgba(2, 6, 23, 0.4), rgba(2, 6, 23, 0.4)), url('/images/bglogout.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          boxSizing: 'border-box',
        }}
      >
        <style>
          {`
            #startup-login-email {
              font-family: Manrope, sans-serif !important;
              font-weight: 400 !important;
              font-size: 22px !important;
              line-height: 100% !important;
              letter-spacing: 0 !important;
              color: #111827 !important;
            }
          `}
        </style>
        <div
          id="startup-login-dialog"
          role="dialog"
          aria-modal="true"
          aria-label="Login"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '845px',
            maxWidth: '845px',
            height: '600px',
            borderRadius: '24px',
            opacity: 1,
            background: '#FFFFFF',
            boxShadow: '0 30px 90px rgba(2, 6, 23, 0.42)',
            border: '1px solid rgba(255,255,255,0.35)',
            overflow: 'hidden',
            display: auth ? 'none' : 'flex',
            flexDirection: 'column',
            fontFamily: 'Manrope, sans-serif',
          }}
        >
          <div id="startup-login-header" style={{ padding: '34px 40px 0 40px', background: '#FFFFFF' }}>
            <h1 id="startup-login-title" style={{ margin: 0, textAlign: 'center', fontSize: '50px', lineHeight: 1.1, fontWeight: 800, color: '#111827' }}>
              GARP Investing Toolbox
            </h1>
            <div id="startup-login-subtitle" style={{ textAlign: 'center', marginTop: '10px', color: '#6B7280', fontSize: '28px', fontWeight: 500 }}>
              Unlimited access to our resources
            </div>
          </div>

          <div id="startup-login-content" style={{ flex: 1, padding: '24px 40px 28px 40px', display: 'grid', gridTemplateColumns: '1fr 1px 1fr', gap: '24px', background: '#FFFFFF' }}>
            <form
              id="startup-login-form"
              onSubmit={handlePopupLoginSubmit}
              autoComplete="off"
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignSelf: 'start',
                justifySelf: 'center',
                paddingTop: '48px',
                width: 'min(320px, 100%)',
                maxWidth: '100%',
                minWidth: 0,
                overflow: 'visible',
              }}
            >
              <div id="startup-login-signup-block" style={{ marginBottom: '12px' }}>
                <div
                  id="startup-login-signup-title"
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 500,
                    fontSize: '24px',
                    lineHeight: '100%',
                    letterSpacing: '0',
                    textAlign: 'center',
                    verticalAlign: 'middle',
                    color: '#111111',
                    marginBottom: '18px',
                  }}
                >
                  Log in
                </div>
                {loginEmailLookupError && (
                  <div
                    id="startup-login-email-error"
                    style={{
                      marginTop: '4px',
                      marginBottom: '12px',
                      marginLeft: '20px',
                      color: '#F44336',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 400,
                      fontSize: '18px',
                      lineHeight: '100%',
                      letterSpacing: '0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <img
                      id="startup-login-email-error-icon"
                      src="/images/Iconalert.svg"
                      alt="Alert"
                      style={{ width: '18px', height: '18px', objectFit: 'contain', flex: '0 0 auto' }}
                    />
                    <span id="startup-login-email-error-text">{loginEmailLookupError}</span>
                  </div>
                )}
                <div
                  id="startup-login-email-wrap"
                  style={{
                    position: 'relative',
                    width: 'calc(100% + 30px)',
                    marginLeft: '-15px',
                    height: '56px',
                    borderRadius: '999px',
                    border: '1px solid #D1D5DB',
                    background: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 14px',
                    boxSizing: 'border-box',
                    gap: '10px',
                  }}
                >
                  <img
                    id="startup-login-google-g"
                    src="/images/Social media logo.svg"
                    alt="Social media logo"
                    style={{ width: '24px', height: '24px', objectFit: 'contain', flex: '0 0 auto' }}
                  />
                    <div style={{ position: 'relative', flex: 1, minWidth: 0, fontSize: '22px' }}>
                    <input
                      id="startup-login-email"
                      ref={loginEmailInputRef}
                      type="email"
                      name="startup_login_email"
                      inputMode="email"
                      value={loginEmail}
                      onInput={(ev) => {
                        if (!isLoginEmailFocused) return;
                        const nextVal = (ev.target as HTMLInputElement).value;
                        setLoginEmail((prev) => (prev !== nextVal ? nextVal : prev));
                      }}
                      onChange={(ev) => {
                        if (!isLoginEmailFocused) return;
                        setLoginEmail(ev.target.value);
                        if (ev.target.value.trim().toLowerCase() === 'asvi@garp.com' && startupSigninUnlockBufferRef.current === 'logmein') {
                          setIsStartupSigninSectionUnlocked(true);
                        }
                        if (isStartupVerificationReady) {
                          setIsStartupVerificationReady(false);
                          setLoginPhoneLast3('');
                          setLoginPhoneNumber('');
                          setLoginVerificationCodeError(null);
                        }
                        if (loginEmailLookupError) setLoginEmailLookupError(null);
                      }}
                      onFocus={() => {
                        setHasLoginEmailInteracted(true);
                        setIsLoginEmailFocused(true);
                        setLoginEmail((prev) => {
                          if (String(prev || '').trim()) return prev;
                          try {
                            return String(localStorage.getItem(LAST_LOGIN_EMAIL_KEY) || '').trim();
                          } catch {
                            return prev;
                          }
                        });
                      }}
                      onBlur={() => setIsLoginEmailFocused(false)}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter') {
                          ev.preventDefault();
                          void handleLookupLoginEmail();
                        }
                      }}
                      placeholder=" "
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      readOnly={!isLoginEmailFocused}
                      style={{
                        width: '100%',
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        color: (!isLoginEmailFocused && (hasLoginEmailInteracted || !!loginEmail)) ? 'transparent' : '#111827',
                        WebkitTextFillColor: (!isLoginEmailFocused && (hasLoginEmailInteracted || !!loginEmail)) ? 'transparent' : '#111827',
                        caretColor: '#111827',
                        opacity: (!isLoginEmailFocused && (hasLoginEmailInteracted || !!loginEmail)) ? 0 : 1,
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '22px',
                        lineHeight: '22px',
                        fontWeight: 400,
                          WebkitTextSizeAdjust: 'none',
                          textSizeAdjust: 'none',
                        }}
                    />
                    <div
                      id="startup-login-email-value-overlay"
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: (hasLoginEmailInteracted && !isLoginEmailFocused && !!loginEmail) ? 'flex' : 'none',
                        alignItems: 'center',
                        pointerEvents: 'none',
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '22px',
                        lineHeight: '22px',
                        fontWeight: 400,
                        color: '#111827',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        userSelect: 'none',
                      }}
                    >
                      {loginEmail}
                    </div>
                    <div
                      id="startup-login-email-fake-placeholder"
                      aria-hidden="true"
                      onClick={() => loginEmailInputRef.current?.focus()}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: ((!hasLoginEmailInteracted || !loginEmail) && !isLoginEmailFocused) ? 'flex' : 'none',
                        alignItems: 'center',
                        pointerEvents: 'none',
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '22px',
                        lineHeight: '22px',
                        fontWeight: 400,
                        color: '#9CA3AF',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        userSelect: 'none',
                      }}
                    >
                      Enter an email address
                    </div>
                  </div>
                </div>
                <div id="startup-login-signup-terms" style={{ marginTop: '10px', color: '#4B5563', fontSize: '11px', lineHeight: 1.35, textAlign: 'left' }}>
                  By signing up, you agree to the Terms of Service and acknowledge you've read our Privacy Policy.
                </div>
                {canShowStartupUlaButton ? (
                  <button
                    id="startup-login-ula-button"
                    type="button"
                    onClick={() => {
                      setUlaSaveError(null);
                      setIsUlaConsentChecked(false);
                      setUlaFirstName('');
                      setUlaLastName('');
                      setIsUlaModalOpen(true);
                    }}
                    style={{
                      marginTop: '10px',
                      border: 'none',
                      borderRadius: '999px',
                      height: '34px',
                      minWidth: '120px',
                      padding: '0 18px',
                      background: isUlaAccepted ? '#16A34A' : '#111827',
                      color: '#FFFFFF',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {isUlaAccepted ? 'ULA Approved' : 'EULA'}
                  </button>
                ) : null}
              </div>

              <div
                id="startup-login-signin-section"
                aria-hidden={!isStartupSigninSectionUnlocked}
                style={{
                  display: isStartupSigninSectionUnlocked ? 'block' : 'none',
                  pointerEvents: isStartupSigninSectionUnlocked ? 'auto' : 'none',
                }}
              >
                <div id="startup-login-form-title" style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '10px' }}>Sign in</div>

                {(loginPopupError || savedStocksError) && (
                  <div id="startup-login-error" style={{ marginBottom: '6px', color: '#DC2626', fontSize: '11px', fontWeight: 600 }}>
                    {loginPopupError || savedStocksError}
                  </div>
                )}

                <label id="startup-login-user-label" htmlFor="startup-login-user" style={{ fontSize: '11px', color: '#6B7280', marginBottom: '4px', fontWeight: 700 }}>
                  User
                </label>
                <select
                  id="startup-login-user"
                  value={loginUser}
                  onChange={(ev) => setLoginUser(ev.target.value)}
                  disabled={authBusy}
                  style={{
                    width: '100%',
                    height: '32px',
                    borderRadius: '8px',
                    border: '1px solid #D1D5DB',
                    background: '#FFFFFF',
                    padding: '0 8px',
                    color: '#111827',
                    fontSize: '12px',
                    fontWeight: 600,
                    marginBottom: '8px',
                  }}
                >
                  {loginUsers.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>

                <label id="startup-login-password-label" htmlFor="startup-login-password" style={{ fontSize: '11px', color: '#6B7280', marginBottom: '4px', fontWeight: 700 }}>
                  Password
                </label>
                <div id="startup-login-password-wrap" style={{ position: 'relative' }}>
                  <input
                    id="startup-login-password"
                    type={showLoginPassword ? 'text' : 'password'}
                    value={loginPassword}
                    onChange={(ev) => setLoginPassword(ev.target.value)}
                    placeholder="Enter password"
                    autoComplete="current-password"
                    disabled={authBusy}
                    required
                    style={{
                      width: '100%',
                      height: '32px',
                      borderRadius: '8px',
                      border: '1px solid #D1D5DB',
                      background: '#FFFFFF',
                      padding: '0 54px 0 8px',
                      color: '#111827',
                      fontSize: '12px',
                      fontWeight: 600,
                      boxSizing: 'border-box',
                    }}
                  />
                  <button
                    id="startup-login-password-toggle"
                    type="button"
                    onClick={() => setShowLoginPassword((prev) => !prev)}
                    disabled={authBusy}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      color: '#2F58F2',
                      fontSize: '10px',
                      fontWeight: 700,
                      cursor: authBusy ? 'not-allowed' : 'pointer',
                      padding: 0,
                    }}
                  >
                    {showLoginPassword ? 'Hide' : 'Show'}
                  </button>
                </div>

                <button
                  id="startup-login-submit"
                  type="submit"
                  disabled={authBusy || !loginPassword.trim()}
                  style={{
                    marginTop: '10px',
                    width: '100%',
                    height: '32px',
                    borderRadius: '8px',
                    border: 'none',
                    background: authBusy || !loginPassword.trim() ? '#D1D5DB' : '#2F58F2',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: 800,
                    cursor: authBusy || !loginPassword.trim() ? 'not-allowed' : 'pointer',
                  }}
                >
                  {authBusy ? 'Connecting…' : 'Next'}
                </button>
              </div>
            </form>

            <div
              id="startup-login-divider"
              style={{
                position: 'relative',
                width: '1px',
                height: '100%',
                background: '#E5E7EB',
                overflow: 'visible',
              }}
            >
              <button
                id="startup-login-divider-arrow-button"
                type="button"
                onClick={() => {
                  void handleLookupLoginEmail();
                }}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '36px',
                  height: '36px',
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  margin: 0,
                  cursor: isLoginEmailLookupBusy ? 'wait' : 'pointer',
                  display: 'block',
                }}
                disabled={isLoginEmailLookupBusy}
                aria-label="Check email and continue"
              >
                <img
                  id="startup-login-divider-arrow"
                  src="/images/login_arrow.svg"
                  alt="Continue"
                  style={{
                    width: '36px',
                    height: '36px',
                    display: 'block',
                  }}
                />
              </button>
            </div>

            <div
              id="startup-login-help"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6B7280',
                fontSize: '14px',
                textAlign: 'left',
              }}
            >
              <div id="startup-login-verification-panel" style={{ width: 'min(360px, 100%)' }}>
                {isStartupVerificationReady ? (
                  <div
                    id="startup-login-verification-sent-box"
                    style={{
                      width: '354px',
                      height: '72px',
                      borderRadius: '6px',
                      opacity: 1,
                      padding: '10px',
                      gap: '10px',
                      background: '#F1F7FF',
                      boxSizing: 'border-box',
                      marginBottom: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: '100%',
                      letterSpacing: '0',
                      textAlign: 'center',
                      color: '#0D0D0D',
                    }}
                  >
                    <div id="startup-login-verification-sent-title">Your verification code was sent by SMS</div>
                    <div id="startup-login-verification-sent-phone">••••••• {loginPhoneLast3 || '---'}</div>
                  </div>
                ) : null}

                <label
                  id="startup-login-phone-label"
                  htmlFor="startup-login-phone"
                  style={{
                    display: 'block',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '100%',
                    letterSpacing: '0',
                    color: '#5A5A5A',
                    marginBottom: '8px',
                  }}
                >
                  Enter the code you received
                </label>
                <input
                  id="startup-login-phone"
                  ref={loginPhoneInputRef}
                  type="tel"
                  value={loginPhoneNumber}
                  onChange={(ev) => {
                    const nextDigits = ev.target.value.replace(/\D/g, '').slice(0, 6);
                    setLoginPhoneNumber(nextDigits);
                    if (loginVerificationCodeError) setLoginVerificationCodeError(null);
                  }}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') {
                      ev.preventDefault();
                      void handleStartupVerificationNext();
                    }
                  }}
                  placeholder=""
                  autoComplete="tel"
                  disabled={!isStartupVerificationReady || isLoginEmailLookupBusy}
                  style={{
                    width: '100%',
                    height: '54px',
                    borderRadius: '12px',
                    border: '1px solid #D1D5DB',
                    background: !isStartupVerificationReady || isLoginEmailLookupBusy ? '#F3F4F6' : '#F9FAFB',
                    padding: '0 14px',
                    boxSizing: 'border-box',
                    fontSize: '16px',
                    color: !isStartupVerificationReady || isLoginEmailLookupBusy ? '#9CA3AF' : '#111827',
                    marginBottom: '22px',
                  }}
                />

                {loginVerificationCodeError ? (
                  <div
                    id="startup-login-verification-code-error"
                    style={{
                      marginTop: '-12px',
                      marginBottom: '12px',
                      color: '#F44336',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '100%',
                      letterSpacing: '0',
                    }}
                  >
                    {loginVerificationCodeError}
                  </div>
                ) : null}

                <button
                  id="startup-login-verification-next"
                  type="button"
                  disabled={!canEnableStartupNext}
                  onClick={() => {
                    void handleStartupVerificationNext();
                  }}
                  style={{
                    width: '180px',
                    height: '64px',
                    borderRadius: '32px',
                    border: 'none',
                    opacity: 1,
                    background: canEnableStartupNext ? '#2F58F2' : '#E0E0E0',
                    color: '#FFFFFF',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 500,
                    fontSize: '24px',
                    lineHeight: '100%',
                    letterSpacing: '0',
                    textAlign: 'center',
                    cursor: canEnableStartupNext ? 'pointer' : 'not-allowed',
                    display: 'block',
                    margin: '0 auto',
                  }}
                >
                  Next
                </button>

                <div
                  id="startup-login-verification-actions"
                  style={{
                    marginTop: '34px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '16px',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '100%',
                    letterSpacing: '0',
                    color: '#2F58F2',
                  }}
                >
                  <button
                    id="startup-login-resend-code"
                    type="button"
                    onClick={() => {
                      void handleLookupLoginEmail();
                    }}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#2F58F2',
                      cursor: isLoginEmailLookupBusy ? 'not-allowed' : 'pointer',
                      padding: 0,
                      margin: 0,
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      lineHeight: 'inherit',
                      letterSpacing: 'inherit',
                    }}
                    disabled={isLoginEmailLookupBusy}
                  >
                    Resend code
                  </button>

                  <button
                    id="startup-login-send-whatsapp"
                    type="button"
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#2F58F2',
                      cursor: 'pointer',
                      padding: 0,
                      margin: 0,
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      lineHeight: 'inherit',
                      letterSpacing: 'inherit',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <img
                      id="startup-login-send-whatsapp-icon"
                      src="/images/whatsapp.svg"
                      alt="WhatsApp"
                      style={{
                        width: '20px',
                        height: '20px',
                        display: 'block',
                        objectFit: 'contain',
                      }}
                    />
                    <span>Send code via WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isUlaModalOpen ? (
          <div
            id="startup-login-ula-overlay"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(2, 6, 23, 0.46)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 30,
              padding: '24px',
              boxSizing: 'border-box',
            }}
          >
            <div
              id="startup-login-ula-modal"
              role="dialog"
              aria-modal="true"
              aria-label="User License Agreement"
              style={{
                width: 'min(760px, 100%)',
                maxHeight: '92%',
                borderRadius: '18px',
                background: '#F3F4F6',
                boxShadow: '0 30px 90px rgba(2, 6, 23, 0.42)',
                border: '1px solid #E5E7EB',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <div style={{ position: 'relative', padding: '22px 28px 0 28px' }}>
                <h2
                  style={{
                    margin: 0,
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontStyle: 'normal',
                    fontSize: '34px',
                    lineHeight: '100%',
                    letterSpacing: '0.25px',
                    textAlign: 'center',
                    verticalAlign: 'middle',
                    color: '#0D0D0D',
                  }}
                >
                  GARP Investing Toolbox
                </h2>
                <div
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 500,
                    fontStyle: 'normal',
                    fontSize: '18px',
                    lineHeight: '100%',
                    letterSpacing: '0',
                    textAlign: 'center',
                    verticalAlign: 'middle',
                    color: '#5A5A5A',
                    marginTop: '8px',
                  }}
                >
                  Unlimited access to our resources
                </div>
              </div>

              <div style={{ padding: '18px 28px 0 28px' }}>
                <div
                  id="startup-login-ula-scroll"
                  onScroll={(ev) => {
                    const target = ev.currentTarget;
                    const atBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 4;
                    if (atBottom) setIsUlaScrolledToBottom(true);
                  }}
                  style={{
                    height: '220px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    background: '#FFFFFF',
                    padding: '14px 16px',
                    overflowY: 'auto',
                    fontSize: '16px',
                    lineHeight: 1.45,
                    color: '#111827',
                    textAlign: 'right',
                    direction: 'rtl',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'Assistant, Segoe UI, Arial, sans-serif',
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: startupEulaHtml }} />
                </div>
              </div>

              <div style={{ padding: '12px 28px 0 28px', direction: 'rtl' }}>
                <label
                  htmlFor="startup-login-ula-consent"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    color: '#111827',
                    fontSize: '14px',
                    lineHeight: 1.45,
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <input
                    id="startup-login-ula-consent"
                    type="checkbox"
                    checked={isUlaConsentChecked}
                    onChange={(ev) => {
                      setIsUlaConsentChecked(ev.target.checked);
                      setIsUlaAccepted(false);
                      if (ulaSaveError) setUlaSaveError(null);
                    }}
                    style={{ marginTop: '2px' }}
                  />
                  <span>
                    אני החתום/ה מטה מאשר/ת כי קראתי את הסכם המנוי, תנאי השימוש וההסתייגות מאחריות, הבנתי את תוכנם, ואני מסכים/ה להיות מחויב/ת להם
                  </span>
                </label>
              </div>

              <div style={{ padding: '12px 28px 0 28px', direction: 'rtl' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label htmlFor="startup-login-ula-first-name" style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
                      שם פרטי
                    </label>
                    <input
                      id="startup-login-ula-first-name"
                      type="text"
                      value={ulaFirstName}
                      onChange={(ev) => {
                        setUlaFirstName(ev.target.value);
                        setIsUlaAccepted(false);
                        if (ulaSaveError) setUlaSaveError(null);
                      }}
                      placeholder="הזן שם פרטי"
                      style={{
                        width: '100%',
                        height: '40px',
                        borderRadius: '8px',
                        border: '1px solid #D1D5DB',
                        background: '#FFFFFF',
                        padding: '0 12px',
                        boxSizing: 'border-box',
                        fontSize: '14px',
                        color: '#111827',
                      }}
                    />
                  </div>
                  <div>
                    <label htmlFor="startup-login-ula-last-name" style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
                      שם משפחה
                    </label>
                    <input
                      id="startup-login-ula-last-name"
                      type="text"
                      value={ulaLastName}
                      onChange={(ev) => {
                        setUlaLastName(ev.target.value);
                        setIsUlaAccepted(false);
                        if (ulaSaveError) setUlaSaveError(null);
                      }}
                      placeholder="הזן שם משפחה"
                      style={{
                        width: '100%',
                        height: '40px',
                        borderRadius: '8px',
                        border: '1px solid #D1D5DB',
                        background: '#FFFFFF',
                        padding: '0 12px',
                        boxSizing: 'border-box',
                        fontSize: '14px',
                        color: '#111827',
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ padding: '14px 28px 0 28px', direction: 'rtl' }}>
                <label htmlFor="startup-login-ula-national-id" style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
                  מספר תעודת זהות
                </label>
                <input
                  id="startup-login-ula-national-id"
                  type="text"
                  inputMode="numeric"
                  value={ulaNationalId}
                  onChange={(ev) => {
                    const digits = ev.target.value.replace(/\D/g, '').slice(0, 9);
                    setUlaNationalId(digits);
                    setIsUlaAccepted(false);
                    if (ulaSaveError) setUlaSaveError(null);
                  }}
                  placeholder="הזן 9 ספרות"
                  style={{
                    width: '100%',
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #D1D5DB',
                    background: '#FFFFFF',
                    padding: '0 12px',
                    boxSizing: 'border-box',
                    fontSize: '14px',
                    color: '#111827',
                  }}
                />
              </div>

              <div style={{ padding: '12px 28px 0 28px', direction: 'rtl' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <label htmlFor="startup-login-ula-signature" style={{ fontSize: '13px', fontWeight: 700, color: '#374151' }}>
                    חתימה דיגיטלית
                  </label>
                  <button
                    id="startup-login-ula-signature-clear"
                    type="button"
                    onClick={clearUlaSignature}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#2F58F2',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    נקה
                  </button>
                </div>
                <canvas
                  id="startup-login-ula-signature"
                  ref={ulaSignatureCanvasRef}
                  width={704}
                  height={120}
                  onMouseDown={(ev) => {
                    const rect = ev.currentTarget.getBoundingClientRect();
                    beginUlaSignature(ev.clientX - rect.left, ev.clientY - rect.top);
                  }}
                  onMouseMove={(ev) => {
                    if (!ulaSignatureDrawingRef.current) return;
                    const rect = ev.currentTarget.getBoundingClientRect();
                    drawOnUlaSignatureCanvas(ev.clientX - rect.left, ev.clientY - rect.top);
                  }}
                  onMouseUp={endUlaSignature}
                  onMouseLeave={endUlaSignature}
                  onTouchStart={(ev) => {
                    ev.preventDefault();
                    const rect = ev.currentTarget.getBoundingClientRect();
                    const touch = ev.touches[0];
                    beginUlaSignature(touch.clientX - rect.left, touch.clientY - rect.top);
                  }}
                  onTouchMove={(ev) => {
                    ev.preventDefault();
                    if (!ulaSignatureDrawingRef.current) return;
                    const rect = ev.currentTarget.getBoundingClientRect();
                    const touch = ev.touches[0];
                    drawOnUlaSignatureCanvas(touch.clientX - rect.left, touch.clientY - rect.top);
                  }}
                  onTouchEnd={endUlaSignature}
                  onTouchCancel={endUlaSignature}
                  style={{
                    width: '100%',
                    height: '120px',
                    borderRadius: '8px',
                    border: '1px solid #D1D5DB',
                    background: '#FFFFFF',
                    display: 'block',
                    touchAction: 'none',
                  }}
                />
              </div>

              <div
                style={{
                  padding: '18px 28px 24px 28px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '18px',
                }}
              >
                <button
                  id="startup-login-ula-accept"
                  type="button"
                  disabled={!canAcceptUla || isUlaSavingPdf}
                  onClick={() => {
                    void handleAcceptUla();
                  }}
                  style={{
                    width: '220px',
                    height: '52px',
                    borderRadius: '26px',
                    border: 'none',
                    background: canAcceptUla && !isUlaSavingPdf ? '#2F58F2' : '#E0E0E0',
                    color: '#FFFFFF',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 500,
                    fontStyle: 'normal',
                    fontSize: '24px',
                    lineHeight: '100%',
                    letterSpacing: '0',
                    textAlign: 'center',
                    verticalAlign: 'middle',
                    cursor: canAcceptUla && !isUlaSavingPdf ? 'pointer' : 'not-allowed',
                  }}
                >
                  {isUlaSavingPdf ? 'שומר...' : 'אישור'}
                </button>
                <button
                  id="startup-login-ula-decline"
                  type="button"
                  onClick={() => {
                    if (isUlaSavingPdf) return;
                    if (auth?.token) {
                      void adminApi.logout(auth.token).catch(() => {
                        // ignore
                      });
                    }
                    setIsUlaAccepted(false);
                    setIsUlaModalOpen(false);
                    setAuth(null);
                  }}
                  style={{
                    width: '154px',
                    height: '52px',
                    borderRadius: '26px',
                    border: 'none',
                    background: '#0D0D0D',
                    color: '#FFFFFF',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 500,
                    fontStyle: 'normal',
                    fontSize: '24px',
                    lineHeight: '100%',
                    letterSpacing: '0',
                    textAlign: 'center',
                    verticalAlign: 'middle',
                    cursor: isUlaSavingPdf ? 'not-allowed' : 'pointer',
                  }}
                  disabled={isUlaSavingPdf}
                >
                  סירוב
                </button>
              </div>
              {ulaSaveError ? (
                <div
                  id="startup-login-ula-save-error"
                  style={{
                    margin: '-12px 28px 20px 28px',
                    textAlign: 'center',
                    color: '#B91C1C',
                    fontSize: '13px',
                    fontWeight: 700,
                  }}
                >
                  {ulaSaveError}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const handleLogout = () => {
    const token = auth?.token;
    if (token) {
      void adminApi.logout(token).catch(() => {
        // Best-effort server logout; local logout should always proceed.
      });
    }
    setAuth(null);
    setSavedStocks([]);
    setSavedStocksError(null);
    stockHistoryCutoffRef.current = 0;
  };

  const handleSaveCurrentStock = async () => {
    if (!auth) {
      setSavedStocksError('Not connected. Click Connect.');
      closeSnackbar();
      return;
    }
    setSavedStocksError(null);
    try {
      const symbol = (state.selectedStock || '').trim().toUpperCase();
      if (!symbol) return;

      // Avoid obvious duplicates in UI
      if (savedStocks.includes(symbol)) return;

      const userId = await ensureUserId(auth).catch(() => null);
      const username = (auth.username || '').trim();
      const request = userId
        ? { userId, stockName: symbol }
        : username
          ? { username, stockName: symbol }
          : null;
      if (!request) {
        setSavedStocksError('Missing user identity for stock history. Please reconnect.');
        closeSnackbar();
        return;
      }
      await adminApi.createStockHistory(auth.token, request);
      await refreshSavedStocks(auth);
      showSnackbar(`${symbol} • ${currentActivityLabel} • Saved`, 'success');
    } catch (e: any) {
      setSavedStocksError(e?.message || 'Failed to save stock');
      closeSnackbar();
    }
  };

  const handleChipAdded = async (symbol: string) => {
    if (!auth) {
      setSavedStocksError('Not connected. Click Connect.');
      return;
    }
    const normalized = (symbol || '').trim().toUpperCase();
    if (!normalized) return;

    try {
      const userId = await ensureUserId(auth).catch(() => null);
      const username = (auth.username || '').trim();
      const request = userId
        ? { userId, stockName: normalized }
        : username
          ? { username, stockName: normalized }
          : null;
      if (!request) {
        setSavedStocksError('Missing user identity for stock history. Please reconnect.');
        return;
      }
      await adminApi.createStockHistory(auth.token, request);
      await refreshSavedStocks(auth);
    } catch (e: any) {
      if (typeof e?.status === 'number' && e.status === 401) {
        setAuth(null);
        setSavedStocksError('Session expired. Please Connect again.');
        return;
      }

      const msg = e?.message || 'Failed saving stock';
      setSavedStocksError(msg);
    }
  };

  const clearLocalChartPrefs = (symbol: string, userId: number | null, username: string | null) => {
    try {
      if (typeof window === 'undefined') return;
      const normalized = (symbol || '').trim().toUpperCase();
      if (!normalized) return;

      const userKeys: string[] = [];
      if (typeof userId === 'number' && Number.isFinite(userId) && userId > 0) {
        userKeys.push(`uid:${userId}`);
      }
      const normalizedUsername = (username || '').trim().toLowerCase();
      if (normalizedUsername) {
        userKeys.push(`user:${normalizedUsername}`);
      }

      if (userKeys.length === 0) return;

      const keysToRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i);
        if (!key) continue;
        if (!key.startsWith('advanced-chart:prefs:')) continue;
        if (!key.endsWith(`:${normalized}`)) continue;

        for (const userKey of userKeys) {
          if (key.includes(`:${userKey}:`)) {
            keysToRemove.push(key);
            break;
          }
        }
      }

      for (const key of keysToRemove) {
        window.localStorage.removeItem(key);
        window.localStorage.setItem(`advanced-chart:purge:${key}`, '1');
      }
    } catch {
      // ignore storage errors (private mode/quota)
    }
  };

  const deleteChartsStateForSymbol = async (symbol: string) => {
    if (!auth) return;
    const normalized = (symbol || '').trim().toUpperCase();
    if (!normalized) return;

    try {
      await adminApi.deleteMyResearchChartsState(auth.token, normalized);
    } catch (e: any) {
      if (typeof e?.status === 'number' && e.status === 401) {
        setAuth(null);
        setSavedStocksError('Session expired. Please Connect again.');
        throw e;
      }
      // best-effort: do not block chip removal on cleanup failures
    }
  };

  const handleChipRemoved = async (symbol: string) => {
    if (!auth) {
      setSavedStocksError('Not connected. Click Connect.');
      throw new Error('Not connected');
    }
    const normalized = (symbol || '').trim().toUpperCase();
    if (!normalized) return;

    try {
      const userId = await ensureUserId(auth).catch(() => null);
      const username = (auth.username || '').trim();
      await adminApi.deleteStockHistoryBySymbol(
        auth.token,
        { userId: userId ?? undefined, username: userId ? undefined : username || undefined },
        normalized
      );
      await deleteChartsStateForSymbol(normalized);
      clearLocalChartPrefs(normalized, userId, auth.username ?? null);
      await refreshSavedStocks(auth);
      showSnackbar(`${normalized} • ${currentActivityLabel} • Deleted`, 'info');
    } catch (e: any) {
      if (typeof e?.status === 'number' && e.status === 401) {
        setAuth(null);
        setSavedStocksError('Session expired. Please Connect again.');
        throw e;
      }

      const msg = e?.message || 'Failed deleting stock';
      setSavedStocksError(msg);
      throw e;
    }
  };

  // App styles
  const styles = {
    app: {
      minHeight: '100vh',
      backgroundColor: state.theme === 'dark' ? '#0F172A' : '#F8FAFC',
      fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      transition: 'background-color 0.3s ease',
    },
    header: {
      padding: '1.5rem',
      backgroundColor: state.theme === 'dark' ? '#1E293B' : '#FFFFFF',
      borderBottom: `1px solid ${state.theme === 'dark' ? '#334155' : '#E2E8F0'}`,
      boxShadow: state.theme === 'dark'
        ? '0 1px 3px 0 rgba(0, 0, 0, 0.3)'
        : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: '800',
      color: state.theme === 'dark' ? '#F1F5F9' : '#0F172A',
      margin: 0,
      textAlign: 'center' as const,
    },
    subtitle: {
      fontSize: '1rem',
      color: state.theme === 'dark' ? '#94A3B8' : '#64748B',
      margin: '8px 0 0 0',
      textAlign: 'center' as const,
    },
    container: {
      display: 'flex',
      padding: '1.5rem',
      gap: '1.5rem',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    mainContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '1.25rem',
     
    },
    chartContainer: {
      backgroundColor: state.theme === 'dark' ? '#1E293B' : '#FFFFFF',
      borderRadius: '0.75rem',
      padding: '1.5rem',
      boxShadow: state.theme === 'dark'
        ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
        : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      border: `1px solid ${state.theme === 'dark' ? '#334155' : '#E2E8F0'}`,
    },
    footer: {
      padding: '1.25rem',
      textAlign: 'center' as const,
      color: state.theme === 'dark' ? '#64748B' : '#94A3B8',
      fontSize: '0.875rem',
      borderTop: `1px solid ${state.theme === 'dark' ? '#334155' : '#E2E8F0'}`,
      backgroundColor: state.theme === 'dark' ? '#1E293B' : '#FFFFFF',
    },
  };

  // Navigation functions
  const handleGoToStockAnalysis = () => {
    setState(prev => ({ ...prev, currentScreen: 'main' }));
  };

  const handleBackToAdvanced = () => {
    setState(prev => ({ ...prev, currentScreen: 'advanced' }));
  };

  const handleToggleTheme = () => {
    setState(prev => ({ ...prev, theme: prev.theme === 'dark' ? 'light' : 'dark' }));
  };

  // Admin screen is a separate entry point (URL-based).
  if (isAdminRoute) {
    return <AdminShell isDarkMode={state.theme === 'dark'} />;
  }

  // If on main screen (stock analysis), render the main screen
  if (state.currentScreen === 'main') {
    return (
      <div style={styles.app}>
        {/* Header */}
        <Header 
          isDarkMode={state.theme === 'dark'}
          onToggleTheme={handleToggleTheme}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          activeTab={activeTab}
          onTabChange={handleResearchTabChange}
          selectedStock={state.selectedStock}
          onSymbolChange={handleHeaderSymbolChange}
          onAddToMyWatchlist={handleAddToMyWatchlist}
          onAddToMyPortfolio={handleAddToMyPortfolio}
          balanceSheetPeriod={balanceSheetPeriod}
          onBalanceSheetPeriodChange={setBalanceSheetPeriod}
          authUser={auth ? { username: auth.username, email: auth.email ?? null, expiresAtUtc: auth.expiresAtUtc } : null}
          authToken={auth?.token}
          onQuickLogin={handleQuickLogin}
          onLogout={handleLogout}
          authBusy={authBusy}
          savedStocks={savedStocks}
          savedStocksError={savedStocksError}
          onSelectSavedStock={handleHeaderSymbolChange}
          onSaveCurrentStock={handleSaveCurrentStock}
          onChipAdded={auth ? handleChipAdded : undefined}
          onChipRemoved={auth ? handleChipRemoved : undefined}
          onNotificationSymbolSelect={handleWatchlistSymbolChange}
        />
        
        <div style={{ padding: '1.5rem 1.5rem 0 1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '16px' }}>
            <button
              onClick={handleBackToAdvanced}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f6';
              }}
            >
              🚀 Advanced Trading Chart
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div style={styles.container}>
          {/* Control Panel */}
          <ControlPanel
            stocks={controlPanelStocks}
            selectedStock={state.selectedStock}
            onStockChange={handleStockChangeWithNavigator}
            chartType={state.chartType}
            onChartTypeChange={handleChartTypeChange}
            indicators={state.indicators}
            onIndicatorChange={handleIndicatorChange}
            theme={state.theme}
            onThemeChange={handleThemeChange}
          />

          {/* Chart Area */}
          <div style={styles.mainContent}>
            {/* Date Range Selector */}
            <DateRangeSelector
              selectedRange={state.selectedRange}
              onRangeChange={handleRangeChange}
              theme={state.theme}
              customStartDate={state.customStartDate}
              customEndDate={state.customEndDate}
              onCustomDateChange={handleCustomDateChange}
            />

            {/* Stock Chart */}
            <StockChart
              data={filteredData}
              technicalIndicators={technicalIndicators}
              chartType={state.chartType}
              showEMA12={state.indicators.showEMA12}
              showEMA26={state.indicators.showEMA26}
              showEMA50={state.indicators.showEMA50}
              showEMA200={state.indicators.showEMA200}
              showVolume={state.indicators.showVolume}
              symbol={state.selectedStock}
              theme={state.theme}
            />

            {/* Date Range Navigator */}
            <DateRangeNavigator
              data={allStockData}
              selectedRange={state.navigatorRange}
              onRangeChange={handleNavigatorRangeChange}
              theme={state.theme}
              symbol={state.selectedStock}
            />

            {/* Stats Display */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginTop: '20px',
            }}>
              {filteredData.length > 0 && (
                <>
                  <div style={{
                    padding: '1rem',
                    backgroundColor: state.theme === 'dark' ? '#1E293B' : '#FFFFFF',
                    borderRadius: '0.5rem',
                    border: `1px solid ${state.theme === 'dark' ? '#334155' : '#E2E8F0'}`,
                  }}>
                    <div style={{
                      fontSize: '0.875rem',
                      color: state.theme === 'dark' ? '#94A3B8' : '#64748B',
                      marginBottom: '4px',
                    }}>
                      Current Price
                    </div>
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: state.theme === 'dark' ? '#F1F5F9' : '#0F172A',
                    }}>
                      ${filteredData[filteredData.length - 1]?.close.toFixed(2)}
                    </div>
                  </div>

                  <div style={{
                    padding: '1rem',
                    backgroundColor: state.theme === 'dark' ? '#1E293B' : '#FFFFFF',
                    borderRadius: '0.5rem',
                    border: `1px solid ${state.theme === 'dark' ? '#334155' : '#E2E8F0'}`,
                  }}>
                    <div style={{
                      fontSize: '0.875rem',
                      color: state.theme === 'dark' ? '#94A3B8' : '#64748B',
                      marginBottom: '4px',
                    }}>
                      Data Points
                    </div>
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: state.theme === 'dark' ? '#F1F5F9' : '#0F172A',
                    }}>
                      {filteredData.length.toLocaleString()}
                    </div>
                  </div>

                  <div style={{
                    padding: '1rem',
                    backgroundColor: state.theme === 'dark' ? '#1E293B' : '#FFFFFF',
                    borderRadius: '0.5rem',
                    border: `1px solid ${state.theme === 'dark' ? '#334155' : '#E2E8F0'}`,
                  }}>
                    <div style={{
                      fontSize: '0.875rem',
                      color: state.theme === 'dark' ? '#94A3B8' : '#64748B',
                      marginBottom: '4px',
                    }}>
                      Date Range
                    </div>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: state.theme === 'dark' ? '#F1F5F9' : '#0F172A',
                    }}>
                      {filteredData[0]?.timestamp.toLocaleDateString()} - {filteredData[filteredData.length - 1]?.timestamp.toLocaleDateString()}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer style={styles.footer}>
          <p>Built with React, TypeScript, and Chart.js • Advanced Financial Analytics</p>
        </footer>

        <WatchlistAddEditModal
          isDarkMode={state.theme === 'dark'}
          open={quickAddWatchlistOpen}
          mode="add"
          prefillSymbol={quickAddWatchlistSymbol}
          authToken={auth?.token}
          onUnauthorized={handleLogout}
          onClose={handleCloseQuickAddWatchlist}
        />

        <QuickAddPortfolioHoldingModal
          isDarkMode={state.theme === 'dark'}
          open={quickAddPortfolioHoldingOpen}
          prefillSymbol={quickAddPortfolioHoldingSymbol}
          authToken={auth?.token}
          onUnauthorized={handleLogout}
          onNotify={showSnackbar}
          onClose={handleCloseQuickAddPortfolioHolding}
          onAdded={({ holdingId, portfolioName }) => {
            const existing = pendingPortfolioHoldingAddRef.current;
            if (existing) window.clearTimeout(existing.timerId);

            const timerId = window.setTimeout(() => {
              pendingPortfolioHoldingAddRef.current = null;
              setPendingPortfolioHoldingAdd(null);
            }, PORTFOLIO_ADD_UNDO_MS);

            const pending = { holdingId, portfolioName, timerId };
            pendingPortfolioHoldingAddRef.current = pending;
            setPendingPortfolioHoldingAdd(pending);
          }}
        />

        {pendingPortfolioHoldingAdd && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed',
              left: '50%',
              bottom: 24,
              transform: 'translateX(-50%)',
              zIndex: 11000,
              width: 450,
              height: 46,
              opacity: 1,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingTop: 13,
                paddingRight: 10,
                paddingBottom: 13,
                paddingLeft: 10,
                boxSizing: 'border-box',
                width: '100%',
                height: '100%',
                borderRadius: 8,
                background: '#F1F7FF',
                color: '#2F58F2',
                boxShadow: '0 12px 40px rgba(17, 24, 39, 0.18)',
              }}
            >
              <div style={{ fontSize: '0.95rem', fontWeight: 600 }}>
                Added to my {pendingPortfolioHoldingAdd.portfolioName}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <button
                  type="button"
                  onClick={() => {
                    const pending = pendingPortfolioHoldingAddRef.current;
                    if (!pending) return;

                    if (!auth?.token) {
                      showSnackbar('You must be logged in to undo.', 'error');
                      dismissPendingPortfolioHoldingAdd();
                      return;
                    }

                    window.clearTimeout(pending.timerId);
                    pendingPortfolioHoldingAddRef.current = null;
                    setPendingPortfolioHoldingAdd(null);

                    void (async () => {
                      try {
                        await adminApi.deleteMyPortfolioHolding(auth.token, pending.holdingId);
                      } catch (e: any) {
                        if (Number(e?.status) === 401) {
                          handleLogout();
                          return;
                        }
                        showSnackbar(e?.message || 'Failed undo', 'error');
                      }
                    })();
                  }}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    color: '#2F58F2',
                  }}
                  title="Undo"
                >
                  <img src="/images/undo.svg" alt="Undo" style={{ width: 18, height: 18, display: 'block' }} />
                  <span>Undo</span>
                </button>

                <button
                  type="button"
                  aria-label="Dismiss"
                  title="Close"
                  onClick={dismissPendingPortfolioHoldingAdd}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                    lineHeight: 1,
                    color: '#2F58F2',
                    opacity: 0.9,
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        )}

        {renderStartupLoginPopup()}
      </div>
    );
  }

  // Render different screens based on currentPage and activeTab
  const renderScreen = () => {
    // If not on Research page, show page-specific screens
    if (currentPage !== 'Research') {
      switch (currentPage) {
        case 'Portfolios':
          return (
            <PortfoliosScreen
              isDarkMode={state.theme === 'dark'}
              onOpenResearch={handleWatchlistSymbolChange}
              authToken={auth?.token}
              authUserId={auth?.userId ?? null}
              onUnauthorized={handleLogout}
              onNotify={showSnackbar}
            />
          );
        case 'Watchlist':
          return (
            <WatchlistScreen
              isDarkMode={state.theme === 'dark'}
              onOpenResearch={handleWatchlistSymbolChange}
              authToken={auth?.token}
              onUnauthorized={handleLogout}
            />
          );
        case 'Alerts':
          return (
            <AlertsScreen
              isDarkMode={state.theme === 'dark'}
              onOpenResearch={handleWatchlistSymbolChange}
              authToken={auth?.token}
              onUnauthorized={handleLogout}
            />
          );
        case 'VInvestAI':
          return isVInvestAIEnabled
            ? <VInvestAIScreen isDarkMode={state.theme === 'dark'} selectedStock={state.selectedStock} />
            : null;
        case 'Recordings':
          return <RecordingsScreen isDarkMode={state.theme === 'dark'} />;
        default:
          return null;
      }
    }
    
    // On Research page, show content based on active tab
    switch (activeTab) {
      case 'Summary':
        return (
          <ResearchSummaryScreen
            isDarkMode={state.theme === 'dark'}
            selectedStock={state.selectedStock}
            isAdmin={isAdminGroup}
            authToken={auth?.token}
            authUserId={auth?.userId ?? null}
            onUnauthorized={handleLogout}
            onNotify={showSnackbar}
          />
        );
      case 'Insights':
        return (
          <ResearchInsightsScreen
            isDarkMode={state.theme === 'dark'}
            selectedStock={state.selectedStock}
            authToken={auth?.token}
            authUserId={auth?.userId ?? null}
            onUnauthorized={handleLogout}
            onNotify={showSnackbar}
          />
        );
      case 'Key Metrics':
        return (
          <ResearchKeyMetricsScreen
            isDarkMode={state.theme === 'dark'}
            selectedStock={state.selectedStock}
            isAdmin={isAdminGroup}
            authToken={auth?.token}
            authUserId={auth?.userId ?? null}
            onUnauthorized={handleLogout}
            onNotify={showSnackbar}
          />
        );
      case 'Balance Sheet':
        return (
          <ResearchBalanceSheetScreen
            isDarkMode={state.theme === 'dark'}
            selectedStock={state.selectedStock}
            selectedPeriod={balanceSheetPeriod}
            onBackToMain={handleGoToStockAnalysis}
            isAdmin={isAdminGroup}
            authToken={auth?.token}
            authUserId={auth?.userId ?? null}
            onUnauthorized={handleLogout}
            onNotify={showSnackbar}
          />
        );
      case 'Income Statement':
        return (
          <ResearchIncomeStatementFiscalScreen
            isDarkMode={state.theme === 'dark'}
            selectedStock={state.selectedStock}
            selectedPeriod={balanceSheetPeriod}
            isAdmin={isAdminGroup}
            authToken={auth?.token}
            authUserId={auth?.userId ?? null}
            onUnauthorized={handleLogout}
            onNotify={showSnackbar}
          />
        );
      case 'Cash Flow':
        return (
          <ResearchCashFlowScreen
            isDarkMode={state.theme === 'dark'}
            selectedStock={state.selectedStock}
            selectedPeriod={balanceSheetPeriod}
            isAdmin={isAdminGroup}
            authToken={auth?.token}
            authUserId={auth?.userId ?? null}
            onUnauthorized={handleLogout}
            onNotify={showSnackbar}
          />
        );
      case 'Growth':
      case 'Growth fiscal':
      case 'Growth Fiscal':
        return (
          <ResearchGrowthFiscalScreen
            isDarkMode={state.theme === 'dark'}
            selectedStock={state.selectedStock}
            selectedPeriod={balanceSheetPeriod}
            isAdmin={isAdminGroup}
            authToken={auth?.token}
            authUserId={auth?.userId ?? null}
            onUnauthorized={handleLogout}
            onNotify={showSnackbar}
          />
        );
      case 'Charts':
        return (
          <AdvancedChartScreenComponent
            isDarkMode={state.theme === 'dark'}
            isAdmin={isAdminGroup}
            onToggleTheme={handleToggleTheme}
            onBackToMain={handleGoToStockAnalysis}
            selectedStock={state.selectedStock}
            authToken={auth?.token}
            authUserId={auth?.userId ?? null}
            authUsername={auth?.username ?? null}
            onUnauthorized={handleLogout}
            onNotify={showSnackbar}
          />
        );
      case 'Chart fpm':
        return (
          <AdvancedChartScreenComponent
            isDarkMode={state.theme === 'dark'}
            isAdmin={isAdminGroup}
            onToggleTheme={handleToggleTheme}
            onBackToMain={handleGoToStockAnalysis}
            selectedStock={state.selectedStock}
            authToken={auth?.token}
            authUserId={auth?.userId ?? null}
            authUsername={auth?.username ?? null}
            onUnauthorized={handleLogout}
            onNotify={showSnackbar}
          />
        );
      case 'Back Testing':
        return (
          <ResearchBackTestingScreen
            isDarkMode={state.theme === 'dark'}
            isAdmin={isAdminGroup}
            onToggleTheme={handleToggleTheme}
            onBackToMain={handleGoToStockAnalysis}
            selectedStock={state.selectedStock}
            selectedPeriod={balanceSheetPeriod}
            financialsSource={backTestingFinancialsSource}
            authToken={auth?.token}
            authUserId={auth?.userId ?? null}
            authUsername={auth?.username ?? null}
            onUnauthorized={handleLogout}
            onNotify={showSnackbar}
          />
        );
      case 'Valuation':
        return (
          <ResearchValuationScreen
            isDarkMode={state.theme === 'dark'}
            selectedStock={state.selectedStock}
            authToken={auth?.token}
            authUserId={auth?.userId ?? null}
            onUnauthorized={handleLogout}
            onNotify={showSnackbar}
          />
        );
      case 'Research Desk':
        return (
          <ResearchDeskScreen
            isDarkMode={state.theme === 'dark'}
            selectedStock={state.selectedStock}
            authToken={auth?.token}
            authUserId={auth?.userId ?? null}
            authUsername={auth?.username ?? null}
            onUnauthorized={handleLogout}
          />
        );
      default:
        return (
          <AdvancedChartScreenComponent
            isDarkMode={state.theme === 'dark'}
            isAdmin={isAdminGroup}
            onToggleTheme={handleToggleTheme}
            onBackToMain={handleGoToStockAnalysis}
            selectedStock={state.selectedStock}
            authToken={auth?.token}
            authUserId={auth?.userId ?? null}
            authUsername={auth?.username ?? null}
            onUnauthorized={handleLogout}
            onNotify={showSnackbar}
          />
        );
    }
  };

  // Default screen is Advanced Trading Chart (Research page)
  return (
    <div style={{ minHeight: '100vh', backgroundColor: state.theme === 'dark' ? '#111827' : '#ffffff' }}>
      <Header 
        isDarkMode={state.theme === 'dark'}
        onToggleTheme={handleToggleTheme}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        activeTab={activeTab}
        onTabChange={handleResearchTabChange}
        selectedStock={state.selectedStock}
        onSymbolChange={handleHeaderSymbolChange}
        onAddToMyWatchlist={handleAddToMyWatchlist}
        onAddToMyPortfolio={handleAddToMyPortfolio}
        authUser={auth ? { username: auth.username, email: auth.email ?? null, expiresAtUtc: auth.expiresAtUtc } : null}
        authToken={auth?.token}
        onQuickLogin={handleQuickLogin}
        onLogout={handleLogout}
        authBusy={authBusy}
        savedStocks={savedStocks}
        savedStocksError={savedStocksError}
        onSelectSavedStock={handleHeaderSymbolChange}
        onSaveCurrentStock={handleSaveCurrentStock}
        onChipAdded={auth ? handleChipAdded : undefined}
        onChipRemoved={auth ? handleChipRemoved : undefined}
        onNotificationSymbolSelect={handleWatchlistSymbolChange}
      />

      <WatchlistAddEditModal
        isDarkMode={state.theme === 'dark'}
        open={quickAddWatchlistOpen}
        mode="add"
        prefillSymbol={quickAddWatchlistSymbol}
        authToken={auth?.token}
        onUnauthorized={handleLogout}
        onClose={handleCloseQuickAddWatchlist}
      />

      <QuickAddPortfolioHoldingModal
        isDarkMode={state.theme === 'dark'}
        open={quickAddPortfolioHoldingOpen}
        prefillSymbol={quickAddPortfolioHoldingSymbol}
        authToken={auth?.token}
        onUnauthorized={handleLogout}
        onNotify={showSnackbar}
        onClose={handleCloseQuickAddPortfolioHolding}
        onAdded={({ holdingId, portfolioName }) => {
          const existing = pendingPortfolioHoldingAddRef.current;
          if (existing) window.clearTimeout(existing.timerId);

          const timerId = window.setTimeout(() => {
            pendingPortfolioHoldingAddRef.current = null;
            setPendingPortfolioHoldingAdd(null);
          }, PORTFOLIO_ADD_UNDO_MS);

          const pending = { holdingId, portfolioName, timerId };
          pendingPortfolioHoldingAddRef.current = pending;
          setPendingPortfolioHoldingAdd(pending);
        }}
      />

      {pendingPortfolioHoldingAdd && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            left: '50%',
            bottom: 24,
            transform: 'translateX(-50%)',
            zIndex: 11000,
            width: 450,
            height: 46,
            opacity: 1,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: 13,
              paddingRight: 10,
              paddingBottom: 13,
              paddingLeft: 10,
              boxSizing: 'border-box',
              width: '100%',
              height: '100%',
              borderRadius: 8,
              background: '#F1F7FF',
              color: '#2F58F2',
              boxShadow: '0 12px 40px rgba(17, 24, 39, 0.18)',
            }}
          >
            <div style={{ fontSize: '0.95rem', fontWeight: 600 }}>
              Added to my {pendingPortfolioHoldingAdd.portfolioName}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <button
                type="button"
                onClick={() => {
                  const pending = pendingPortfolioHoldingAddRef.current;
                  if (!pending) return;

                  if (!auth?.token) {
                    showSnackbar('You must be logged in to undo.', 'error');
                    dismissPendingPortfolioHoldingAdd();
                    return;
                  }

                  window.clearTimeout(pending.timerId);
                  pendingPortfolioHoldingAddRef.current = null;
                  setPendingPortfolioHoldingAdd(null);

                  void (async () => {
                    try {
                      await adminApi.deleteMyPortfolioHolding(auth.token, pending.holdingId);
                    } catch (e: any) {
                      if (Number(e?.status) === 401) {
                        handleLogout();
                        return;
                      }
                      showSnackbar(e?.message || 'Failed undo', 'error');
                    }
                  })();
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  color: '#2F58F2',
                }}
                title="Undo"
              >
                <img src="/images/undo.svg" alt="Undo" style={{ width: 18, height: 18, display: 'block' }} />
                <span>Undo</span>
              </button>

              <button
                type="button"
                aria-label="Dismiss"
                title="Close"
                onClick={dismissPendingPortfolioHoldingAdd}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  lineHeight: 1,
                  color: '#2F58F2',
                  opacity: 0.9,
                }}
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {renderStartupLoginPopup()}

      {renderScreen()}
      {config.ui?.showStatusSnackbar ? (
        <Snackbar
          isDarkMode={state.theme === 'dark'}
          open={snackbar.open}
          message={snackbar.message}
          variant={snackbar.variant}
          onClose={closeSnackbar}
        />
      ) : null}
    </div>
  );
};

export default App;





